import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, 
  Trash2, 
  Pencil, 
  ArrowLeft, 
  Check, 
  ShieldAlert,
  Info,
  UserPlus,
  ShieldCheck,
  KeyRound,
  Tv,
  Calendar,
  Image as ImageIcon,
  MessageSquare,
  AlertTriangle,
  Volume2,
  ExternalLink,
  Plus,
  RotateCcw,
  ChevronLeft,
  Clock,
  Gauge,
  TrendingUp,
  FileText,
  Printer,
  Upload
} from 'lucide-react';
import { SectorItem, SectorPermission } from './settings/settings.types';
import { SettingsController } from './settings/settings.controller';
import { UserItem } from './settings/users.types';
import { UsersController } from './settings/users.controller';
import { StatusItem } from './settings/status.types';
import { StatusController } from './settings/status.controller';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { useApp } from '../context/AppContext';
import SystemLogView from '../components/settings/SystemLogView';
import OccurrencesView from '../components/settings/OccurrencesView';

interface TvMessage {
  id: number;
  type: 'TEXT' | 'IMAGE' | 'FULL_SCREEN_IMAGE';
  title?: string;
  body?: string;
  imageUrl?: string;
  daysOfWeek?: string[];
  instantExhibition: boolean;
  isActive: boolean;
  classification: 'NORMAL' | 'URGENTE' | 'EMERGENCIA';
}

export default function SettingsPageView() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState<'DASHBOARD' | 'SETORES' | 'USUARIOS' | 'USUARIO_EDIT' | 'STATUS_LIST' | 'STATUS_EDIT' | 'PROJECTION_MGR' | 'DASHBOARD_PERF_MGR' | 'REPORTS_MGR' | 'SYSTEM_LOGS' | 'OCCURRENCES'>('DASHBOARD');

  // Reset screen view back to Dashboard when clicking Settings sidebar link or routing occurs
  useEffect(() => {
    setActiveView('DASHBOARD');
  }, [location]);

  useEffect(() => {
    async function loadSectors() {
      const data = await SettingsController.getSectorsFromServer();
      setSectorsList(data);
    }
    loadSectors();
    StatusController.fetchList().then(list => setStatusList(list));
  }, []);

  const [sectorsList, setSectorsList] = useState<SectorItem[]>(() => SettingsController.getSectors());
  const [usersList, setUsersList] = useState<UserItem[]>(() => UsersController.list());
  const [statusList, setStatusList] = useState<StatusItem[]>(() => StatusController.list());
  const [editingStatus, setEditingStatus] = useState<StatusItem | null>(null);
  
  // Filtering states for the users list
  const [filterStatus, setFilterStatus] = useState<string>('Todos');
  const [filterName, setFilterName] = useState<string>('');
  const [filterSector, setFilterSector] = useState<string>('');

  const [editingSector, setEditingSector] = useState<SectorItem | null>(null);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [alertState, setAlertState] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  // TV / PROJECTION MESSAGE STATES & HOOKS
  const [tvMessages, setTvMessages] = useState<TvMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isEditingMessage, setIsEditingMessage] = useState<TvMessage | null>(null);

  // TV / PROJECTION CONFIGURATION STATES
  const [projDateFilter, setProjDateFilter] = useState<'CURRENT_MONTH' | 'CURRENT_MONTH_NEXT_FORTNIGHT' | 'CURRENT_MONTH_NEXT_MONTH'>('CURRENT_MONTH_NEXT_FORTNIGHT');
  const [projPageSize, setProjPageSize] = useState<number>(12);
  const [projSlideInterval, setProjSlideInterval] = useState<number>(8);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

  // PERFORMANCE DASHBOARD CONFIGURATION STATES
  const [dashActivePreset, setDashActivePreset] = useState<'SETORES_GARGALOS' | 'SLAS_ENTREGAS' | 'VOLUME_METAS'>('SETORES_GARGALOS');
  const [dashDateRangeDays, setDashDateRangeDays] = useState<number>(7);
  const [isUpdatingDashConfig, setIsUpdatingDashConfig] = useState(false);

  // REPORTS GLOBAL CONFIGURATION STATES
  const [repCompanyName, setRepCompanyName] = useState<string>('Master Coifa Ltda • Soluções em Aço Inox');
  const [repLayoutType, setRepLayoutType] = useState<'CLASSIFICACAO_CRITICA' | 'SIMPLIFICADO_RESUMENTE' | 'DETALHADO_AUDITORIA'>('CLASSIFICACAO_CRITICA');
  const [repFooterText, setRepFooterText] = useState<string>('Relatório operacional sigiloso para controle de fluxo físico.');
  const [repIncludeAuditLog, setRepIncludeAuditLog] = useState<boolean>(true);
  const [isUpdatingRepConfig, setIsUpdatingRepConfig] = useState(false);

  // Form states for creating/editing messages
  const [msgFormType, setMsgFormType] = useState<'TEXT' | 'IMAGE' | 'FULL_SCREEN_IMAGE'>('TEXT');
  const [msgFormTitle, setMsgFormTitle] = useState('');
  const [msgFormBody, setMsgFormBody] = useState('');
  const [msgFormImageUrl, setMsgFormImageUrl] = useState('');
  const [msgFormDays, setMsgFormDays] = useState<string[]>([]);
  const [msgFormInstant, setMsgFormInstant] = useState(true);
  const [msgFormClassification, setMsgFormClassification] = useState<'NORMAL' | 'URGENTE' | 'EMERGENCIA'>('NORMAL');

  const fetchTvMessages = async () => {
    setIsLoadingMessages(true);
    try {
      const response = await api.get<TvMessage[]>('/tv-messages');
      setTvMessages(response.data || []);
    } catch (err) {
      console.error(err);
      triggerAlert('danger', 'Erro ao carregar mensagens da TV de projeção.');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const fetchProjectorConfig = async () => {
    try {
      const response = await api.get<{ dateFilter: any; pageSize: number; slideInterval: number }>('/projector-config');
      if (response.data) {
        setProjDateFilter(response.data.dateFilter);
        setProjPageSize(response.data.pageSize);
        setProjSlideInterval(response.data.slideInterval);
      }
    } catch (err) {
      console.error('Error fetching projector config', err);
    }
  };

  const fetchDashboardConfig = async () => {
    try {
      const response = await api.get<{ activePreset: 'SETORES_GARGALOS' | 'SLAS_ENTREGAS' | 'VOLUME_METAS'; dateRangeDays: number }>('/dashboard-config');
      if (response.data) {
        setDashActivePreset(response.data.activePreset);
        setDashDateRangeDays(response.data.dateRangeDays);
      }
    } catch (err) {
      console.error('Error fetching dashboard config', err);
    }
  };

  const handleUpdateProjectorConfig = async (
    dateFilter: string,
    pageSize: number,
    slideInterval: number
  ) => {
    setIsUpdatingConfig(true);
    try {
      await api.put('/projector-config', {
        dateFilter,
        pageSize,
        slideInterval
      });
      setProjDateFilter(dateFilter as any);
      setProjPageSize(pageSize);
      setProjSlideInterval(slideInterval);
      triggerAlert('success', 'Configurações globais do projetor atualizadas com sucesso!');
    } catch (err) {
      console.error('Error updating projector config', err);
      triggerAlert('danger', 'Erro ao salvar configurações do projetor.');
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const handleUpdateDashboardConfig = async (
    activePreset: string,
    dateRangeDays: number
  ) => {
    setIsUpdatingDashConfig(true);
    try {
      await api.put('/dashboard-config', {
        activePreset,
        dateRangeDays
      });
      setDashActivePreset(activePreset as any);
      setDashDateRangeDays(dateRangeDays);
      triggerAlert('success', 'Layout padrão do painel de desempenho salvo com sucesso!');
    } catch (err) {
      console.error('Error updating dashboard config', err);
      triggerAlert('danger', 'Erro ao salvar configuração do painel de desempenho.');
    } finally {
      setIsUpdatingDashConfig(false);
    }
  };

  const fetchReportsConfig = async () => {
    try {
      const response = await api.get<{ companyName: string; layoutType: any; footerText: string; includeAuditLog: boolean }>('/reports-config');
      if (response.data) {
        setRepCompanyName(response.data.companyName);
        setRepLayoutType(response.data.layoutType);
        setRepFooterText(response.data.footerText);
        setRepIncludeAuditLog(response.data.includeAuditLog);
      }
    } catch (err) {
      console.error('Error fetching reports config', err);
    }
  };

  const handleUpdateReportsConfig = async (
    companyName: string,
    layoutType: string,
    footerText: string,
    includeAuditLog: boolean
  ) => {
    setIsUpdatingRepConfig(true);
    try {
      await api.put('/reports-config', {
        companyName,
        layoutType,
        footerText,
        includeAuditLog
      });
      setRepCompanyName(companyName);
      setRepLayoutType(layoutType as any);
      setRepFooterText(footerText);
      setRepIncludeAuditLog(includeAuditLog);
      triggerAlert('success', 'Configurações globais de relatórios salvas com sucesso!');
    } catch (err) {
      console.error('Error updating reports config', err);
      triggerAlert('danger', 'Erro ao salvar configurações do modelo de relatório.');
    } finally {
      setIsUpdatingRepConfig(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const u = await UsersController.listFromServer();
      setUsersList(u);
    } catch (err) {
      console.error('Erro ao sincronizar usuários:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeView === 'PROJECTION_MGR') {
      fetchTvMessages();
      fetchProjectorConfig();
    } else if (activeView === 'DASHBOARD_PERF_MGR') {
      fetchDashboardConfig();
    } else if (activeView === 'REPORTS_MGR') {
      fetchReportsConfig();
    } else if (activeView === 'USUARIOS') {
      fetchUsers();
    }
  }, [activeView]);

  // Column levels mapping
  const columns = [
    { key: 'view', label: 'Visualiza' },
    { key: 'add', label: 'Adiciona' },
    { key: 'edit', label: 'Edita' },
    { key: 'delete', label: 'Exclui' },
    { key: 'export', label: 'Exporta' }
  ] as const;

  // Entity resource mapping
  const entities = [
    { key: 'pedido', label: 'Pedido' },
    { key: 'produto', label: 'Produto' },
    { key: 'categoria', label: 'Categoria' },
    { key: 'status', label: 'Status' },
    { key: 'usuario', label: 'Usuário' },
    { key: 'setor', label: 'Setor' },
    { key: 'configuracao', label: 'Configuração' },
    { key: 'relatorios', label: 'Relatórios' }
  ] as const;

  const triggerAlert = (type: 'success' | 'danger', message: string) => {
    setAlertState({ type, message });
    setTimeout(() => {
      setAlertState(null);
    }, 4500);
  };

  // ==========================================
  // SECTORS FUNCTIONAL CONTROLS
  // ==========================================
  const handleSaveSectorSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const sectorName = formData.get('sectorName') as string;

    if (!sectorName || !sectorName.trim()) {
      triggerAlert('danger', 'O nome do setor é de preenchimento obrigatório.');
      return;
    }

    const permissions: SectorPermission = SettingsController.getEmptyPermissions();
    columns.forEach(col => {
      entities.forEach(ent => {
        const checkboxKey = `perm_${col.key}_${ent.key}`;
        permissions[col.key][ent.key] = formData.get(checkboxKey) === 'on';
      });
    });

    const isEdit = !!editingSector;
    SettingsController.saveSectorToServer(
      editingSector ? editingSector.id : null, 
      sectorName.trim(), 
      permissions
    ).then(updatedList => {
      setSectorsList(updatedList);
      setEditingSector(null);
      e.currentTarget.reset();

      triggerAlert(
        'success', 
        isEdit 
          ? `Setor "${sectorName}" atualizado com sucesso com suas novas pré-configurações de segurança!` 
          : `Setor "${sectorName}" criado e associado a novas diretivas de acesso com êxito.`
      );
    });
  };

  const handleDeleteSectorClick = (id: string, name: string) => {
    if (window.confirm(`Deseja realmente excluir o setor "${name}"? Todas as suas diretivas pré-configuradas serão removidas.`)) {
      SettingsController.deleteSectorFromServer(id).then(updatedList => {
        setSectorsList(updatedList);
        if (editingSector && editingSector.id === id) {
          setEditingSector(null);
        }
        triggerAlert('success', `Setor "${name}" foi permanentemente removido.`);
      });
    }
  };

  const handleEditSectorClick = (sector: SectorItem) => {
    setEditingSector(sector);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelSectorForm = () => {
    setEditingSector(null);
    const formElement = document.getElementById('sector-registration-form') as HTMLFormElement;
    if (formElement) formElement.reset();
  };

  const handleSelectAllSectorCheckboxes = (check: boolean) => {
    const formElement = document.getElementById('sector-registration-form') as HTMLFormElement;
    if (!formElement) return;

    columns.forEach(col => {
      entities.forEach(ent => {
        const checkbox = formElement.querySelector(`input[name="perm_${col.key}_${ent.key}"]`) as HTMLInputElement | null;
        if (checkbox) {
          checkbox.checked = check;
        }
      });
    });
  };

  // ==========================================
  // USERS FUNCTIONAL CONTROLS
  // ==========================================
  const handleSaveUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const fullName = formData.get('fullName') as string;
    const sector = formData.get('sector') as string;
    const email = formData.get('email') as string;
    const username = formData.get('username') as string;
    const cpf = formData.get('cpf') as string;
    const status = (formData.get('status') as 'ATIVO' | 'INATIVO') || 'ATIVO';

    if (!fullName || !fullName.trim()) {
      triggerAlert('danger', 'O nome completo do colaborador é obrigatório.');
      return;
    }
    if (!sector || !sector.trim()) {
      triggerAlert('danger', 'Por favor, selecione um setor de trabalho.');
      return;
    }
    if (!email || !email.trim()) {
      triggerAlert('danger', 'O e-mail para identificação profissional é obrigatório.');
      return;
    }
    if (!username || !username.trim()) {
      triggerAlert('danger', 'O nome de usuário para acesso login é obrigatório.');
      return;
    }

    // Accumulate checkbox states into beautiful permissions object for the user profile
    const permissions: SectorPermission = SettingsController.getEmptyPermissions();
    columns.forEach(col => {
      entities.forEach(ent => {
        const checkboxKey = `userper_${col.key}_${ent.key}`;
        permissions[col.key][ent.key] = formData.get(checkboxKey) === 'on';
      });
    });

    const isEdit = !!editingUser;
    const updatedUsers = await UsersController.saveToServer(
      editingUser ? editingUser.id : null,
      fullName.trim(),
      sector,
      email.trim(),
      username.trim(),
      cpf ? cpf.trim() : '',
      permissions,
      status
    );

    setUsersList(updatedUsers);
    setEditingUser(null);
    setActiveView('USUARIOS');

    triggerAlert(
      'success', 
      isEdit 
         ? `Colaborador "${fullName}" atualizado com sucesso com suas novas heranças de acesso!` 
         : `Colaborador "${fullName}" registrado com sucesso no setor de ${sector}.`
    );
  };

  const handleDeleteUserClick = async (id: string, name: string) => {
    if (window.confirm(`Deseja permanentemente remover o usuário colaborador "${name}"? Esta ação removerá o seu acesso ao sistema de produção.`)) {
      const updatedUsers = await UsersController.deleteFromServer(id);
      setUsersList(updatedUsers);
      triggerAlert('success', `Colaborador "${name}" removido com sucesso.`);
    }
  };

  // Helper function to dynamically load pre-configured permissions from selected Sector
  const applyPresetFromSector = (sectorName: string) => {
    const matchedSector = sectorsList.find(s => s.name.toLowerCase() === sectorName.toLowerCase());
    
    const formElement = document.getElementById('user-registration-form') as HTMLFormElement;
    if (!formElement) return;

    columns.forEach(col => {
      entities.forEach(ent => {
        const checkbox = formElement.querySelector(`input[name="userper_${col.key}_${ent.key}"]`) as HTMLInputElement | null;
        if (checkbox) {
          checkbox.checked = matchedSector ? !!matchedSector.permissions[col.key][ent.key] : false;
        }
      });
    });

    if (matchedSector) {
      triggerAlert('success', `Permissões pré-configuradas do setor "${sectorName}" foram carregadas com sucesso.`);
    }
  };

  const handleSelectAllUserCheckboxes = (check: boolean) => {
    const formElement = document.getElementById('user-registration-form') as HTMLFormElement;
    if (!formElement) return;

    columns.forEach(col => {
      entities.forEach(ent => {
        const checkbox = formElement.querySelector(`input[name="userper_${col.key}_${ent.key}"]`) as HTMLInputElement | null;
        if (checkbox) {
          checkbox.checked = check;
        }
      });
    });
  };

  // ==========================================
  // STATUS FUNCTIONAL CONTROLS
  // ==========================================
  const handleSaveStatusSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const nameStr = formData.get('statusName') as string;

    if (!nameStr || !nameStr.trim()) {
      triggerAlert('danger', 'O nome do status é de preenchimento obrigatório.');
      return;
    }

    const updated = await StatusController.save(
      editingStatus ? editingStatus.id : null,
      nameStr
    );

    setStatusList(updated);
    setEditingStatus(null);
    setActiveView('STATUS_LIST');

    triggerAlert(
      'success',
      editingStatus
        ? `Status "${nameStr}" atualizado com sucesso!`
        : `Novo status personalizado "${nameStr}" criado com sucesso!`
    );
  };

  const handleDeleteStatusClick = async (id: string, name: string) => {
    if (window.confirm(`Deseja realmente excluir permanentemente o status "${name}"? Esta ação não pode ser desfeita.`)) {
      const updated = await StatusController.delete(id, name);
      setStatusList(updated);
      triggerAlert('success', `Status "${name}" foi permanentemente removido.`);
    }
  };

  // Filter submit and clear logic
  const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setFilterStatus((formData.get('filterStatus') as string) || 'Todos');
    setFilterName((formData.get('filterName') as string) || '');
    setFilterSector((formData.get('filterSector') as string) || '');
  };

  const handleClearFilters = () => {
    setFilterStatus('Todos');
    setFilterName('');
    setFilterSector('');
    const formElement = document.getElementById('users-filter-form') as HTMLFormElement;
    if (formElement) formElement.reset();
  };

  // Evaluates permissions levels: returns 'Tudo', 'Parcial', or 'Nenhum'
  const getPermissionGroupLabel = (permsObj: Record<string, boolean> | undefined) => {
    if (!permsObj) return 'Nenhum';
    const keys = Object.keys(permsObj);
    if (keys.length === 0) return 'Nenhum';
    const checkedCount = keys.filter(k => permsObj[k]).length;
    if (checkedCount === keys.length) return 'Tudo';
    if (checkedCount > 0) return 'Parcial';
    return 'Nenhum';
  };

  // Inline filter execution
  const filteredUsers = usersList.filter(user => {
    const matchStatus = filterStatus === 'Todos' || user.status === filterStatus;
    const matchName = !filterName || user.fullName.toLowerCase().includes(filterName.toLowerCase());
    const matchSector = !filterSector || user.sector.toLowerCase().includes(filterSector.toLowerCase());
    return matchStatus && matchName && matchSector;
  });

  // Shorten Sector string helper for strict matching representation as "Adm"
  const getSectorShortName = (secName: string) => {
    if (secName.toLowerCase().startsWith('adm')) return 'Adm';
    return secName;
  };


  // ==========================================
  // VIEW RENDERERS
  // ==========================================

  // 1. RENDER COLLABORATORS LIST VIEW (REFACTORED ACCORDING TO USER'S NEW IMAGE LAYOUT)
  const renderUsersListPanel = () => {
    return (
      <div id="users-configuration-panel" className="animate-fade-in">
        
        {/* Breadcrumb Path Bar & Beautiful Top Actions */}
        <div className="d-flex flex-row justify-content-between align-items-center border-bottom pb-3 mb-3">
          <div>
            <div className="text-secondary text-xs font-mono font-bold tracking-wider" style={{ color: '#212529', opacity: 0.85 }}>
              Configurações &gt; Lista de usuários
            </div>
          </div>

          <div>
            <button 
              type="button" 
              onClick={() => { setEditingUser(null); setActiveView('USUARIO_EDIT'); }}
              className="btn btn-primary px-3 py-1.5 font-display text-sm text-white fw-semibold d-flex align-items-center gap-1.5"
              style={{ backgroundColor: '#4a89dc', borderColor: '#4a89dc', borderRadius: '4px' }}
            >
              + Adicionar usuário
            </button>
          </div>
        </div>

        {/* REFACTORED FILTERS SEARCH CARD (Matches layout exactly) */}
        <form id="users-filter-form" onSubmit={handleFilterSubmit} className="bg-light p-3 rounded-3 mb-3 border">
          <div className="row g-2 align-items-end">
            
            {/* Status Selector dropdown */}
            <div className="col-12 col-md-3">
              <label className="text-secondary font-sans fw-semibold mb-1 d-block" style={{ fontSize: '13px', color: '#6c757d' }}>Status</label>
              <select 
                name="filterStatus" 
                className="form-select bg-white font-sans text-xs"
                style={{ height: '38px', borderRadius: '4px', border: '1px solid #ced4da' }}
                defaultValue={filterStatus}
              >
                <option value="Todos">Todos</option>
                <option value="ATIVO">ATIVO</option>
                <option value="INATIVO">INATIVO</option>
              </select>
            </div>

            {/* Nome do Usuário Text Input */}
            <div className="col-12 col-md-3">
              <label className="text-secondary font-sans fw-semibold mb-1 d-block" style={{ fontSize: '13px', color: '#6c757d' }}>Nome do Usuário</label>
              <input 
                type="text" 
                name="filterName"
                placeholder="Ex. Paulo Sérgio"
                defaultValue={filterName}
                className="form-control bg-white font-sans text-xs"
                style={{ height: '38px', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            {/* Setor Text Input */}
            <div className="col-12 col-md-3">
              <label className="text-secondary font-sans fw-semibold mb-1 d-block" style={{ fontSize: '13px', color: '#6c757d' }}>Setor</label>
              <input 
                type="text" 
                name="filterSector"
                placeholder="Ex. Corte"
                defaultValue={filterSector}
                className="form-control bg-white font-sans text-xs"
                style={{ height: '38px', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            {/* Actions Filters buttons group */}
            <div className="col-12 col-md-3 d-flex gap-2">
              <button 
                type="button" 
                onClick={handleClearFilters}
                className="btn btn-outline-secondary font-sans text-xs fw-semibold w-50"
                style={{ height: '38px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: '#fff' }}
              >
                Limpar filtros
              </button>
              <button 
                type="submit" 
                className="btn btn-primary font-sans text-xs fw-semibold w-50 text-white"
                style={{ height: '38px', borderRadius: '4px', backgroundColor: '#4a89dc', borderColor: '#4a89dc' }}
              >
                Filtrar
              </button>
            </div>

          </div>
        </form>

        {/* Global actions response notifier toast */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-xs d-flex align-items-center gap-2 mb-3 py-2 px-3`} role="alert">
            <AlertCircle size={16} />
            <span className="font-sans text-xs fw-semibold">{alertState.message}</span>
          </div>
        )}

        {/* USERS REFACTORED MATCHING LIST TABLE - DESKTOP VIEW */}
        <div className="card shadow-xs borderRounded-0 rounded-2 bg-white p-0 overflow-hidden mb-4 d-none d-lg-block">
          <div className="table-responsive">
            <table className="table align-middle table-hover mb-0 text-dark">
              <thead className="table-light border-bottom text-dark" style={{ backgroundColor: '#f8f9fa' }}>
                <tr className="font-sans fw-bold text-sm" style={{ letterSpacing: '0.2px' }}>
                  <th scope="col" className="ps-3 py-2.5" style={{ color: '#000', fontWeight: 'bold' }}>Usuário</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Setor</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Visualiza</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Adiciona</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Edita</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Exclui</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Exporta</th>
                  <th scope="col" className="py-2.5 text-center" style={{ width: '100px', color: '#000', fontWeight: 'bold' }}>Status</th>
                  <th scope="col" className="pe-3 py-2.5 text-end" style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody className="border-0 font-sans text-xs">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const viewGroup = getPermissionGroupLabel(user.permissions?.view);
                    const addGroup = getPermissionGroupLabel(user.permissions?.add);
                    const editGroup = getPermissionGroupLabel(user.permissions?.edit);
                    const delGroup = getPermissionGroupLabel(user.permissions?.delete);
                    const expGroup = getPermissionGroupLabel(user.permissions?.export);
                    const isUserActive = user.status === 'ATIVO';

                    return (
                      <tr key={user.id} className="border-bottom hover-bg-light transition-all">
                        {/* 1. ID + Full name inside single matching TD */}
                        <td className="ps-3 py-3">
                          <span className="font-mono text-secondary me-3 text-xxs fw-bold" style={{ width: '24px', display: 'inline-block' }}>
                            {user.id}
                          </span>
                          <span className="fw-semibold text-dark font-sans" style={{ fontSize: '13px' }}>
                            {user.fullName}
                          </span>
                        </td>

                        {/* 2. Sector matching e.g. "Adm", "Corte" */}
                        <td className="py-3 text-center text-muted fw-semibold">
                          {getSectorShortName(user.sector)}
                        </td>

                        {/* 3. Permissions matrices evaluation fields */}
                        <td className="py-3 text-center text-secondary">
                          {viewGroup}
                        </td>
                        <td className="py-3 text-center text-secondary">
                          {addGroup}
                        </td>
                        <td className="py-3 text-center text-secondary">
                          {editGroup}
                        </td>
                        <td className="py-3 text-center text-secondary">
                          {delGroup}
                        </td>
                        <td className="py-3 text-center text-secondary">
                          {expGroup}
                        </td>

                        {/* 4. Custom bold uppercase color status matches image (ATIVO/INATIVO) */}
                        <td className="py-3 text-center">
                          {isUserActive ? (
                            <span className="badge px-3 py-1.5 fw-bold text-white text-xxs" style={{ backgroundColor: '#00a651', minWidth: '70px', borderRadius: '4px' }}>
                              ATIVO
                            </span>
                          ) : (
                            <span className="badge px-3 py-1.5 fw-bold text-white text-xxs" style={{ backgroundColor: '#a6a6a6', minWidth: '70px', borderRadius: '4px' }}>
                              INATIVO
                            </span>
                          )}
                        </td>

                        {/* 5. Precise aligned pencil and red trash actions icons */}
                        <td className="pe-3 py-2 text-end">
                          <div className="d-flex align-items-center justify-content-end gap-1">
                            <button 
                              type="button" 
                              onClick={() => { setEditingUser(user); setActiveView('USUARIO_EDIT'); }}
                              title="Editar Usuário"
                              className="btn btn-link link-secondary border-0 p-1.5 d-flex align-items-center justify-content-center hover-bg"
                              style={{ color: '#2f66aa' }}
                            >
                              <Pencil size={17} style={{ color: '#2f66aa' }} />
                            </button>
                            
                            {/* Administrators safety check */}
                            {user.id !== '23' ? (
                              <button 
                                type="button" 
                                onClick={() => handleDeleteUserClick(user.id, user.fullName)}
                                title="Excluir Usuário"
                                className="btn btn-link link-danger p-1.5 border-0 d-flex align-items-center justify-content-center hover-bg"
                                style={{ color: '#dc3545' }}
                              >
                                <Trash2 size={17} style={{ color: '#dc3545' }} />
                              </button>
                            ) : (
                              <span className="p-1.5 text-muted d-inline-block" title="Administrador Master não pode ser removido.">
                                <ShieldCheck size={16} className="text-success" />
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center p-5 text-muted font-sans">
                      Nenhum colaborador corresponde aos filtros ativos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* USERS RESPONSIVE CARDS VIEW - MOBILE/TABLET */}
        <div className="d-block d-lg-none" id="users-mobile-list">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => {
              const viewGroup = getPermissionGroupLabel(user.permissions?.view);
              const addGroup = getPermissionGroupLabel(user.permissions?.add);
              const editGroup = getPermissionGroupLabel(user.permissions?.edit);
              const delGroup = getPermissionGroupLabel(user.permissions?.delete);
              const expGroup = getPermissionGroupLabel(user.permissions?.export);
              const isUserActive = user.status === 'ATIVO';

              return (
                <div 
                  key={user.id} 
                  className="card border mb-3 shadow-xs bg-white p-3 rounded-2"
                  style={{ borderColor: '#e4e7eb' }}
                >
                  {/* Top line: Name + ID and Status Badge */}
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div className="min-w-0">
                      <span className="font-mono text-secondary text-xxs fw-bold">ID #{user.id}</span>
                      <h4 className="font-sans text-sm fw-bold text-dark mb-0 text-truncate">{user.fullName}</h4>
                      <div className="mt-1 d-flex gap-1.5 align-items-center">
                        <span className="badge bg-secondary bg-opacity-15 text-dark fw-bold text-xxs font-sans py-1 px-2 border">
                          Setor: {getSectorShortName(user.sector)}
                        </span>
                      </div>
                    </div>
                    <div>
                      {isUserActive ? (
                        <span className="badge px-2.5 py-1 fw-bold text-white text-xxs" style={{ backgroundColor: '#00a651', borderRadius: '4px' }}>
                          ATIVO
                        </span>
                      ) : (
                        <span className="badge px-2.5 py-1 fw-bold text-white text-xxs" style={{ backgroundColor: '#a6a6a6', borderRadius: '4px' }}>
                          INATIVO
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Permissions grid / tags */}
                  <div className="bg-light rounded p-2 mb-2.5" style={{ fontSize: '0.75rem' }}>
                    <div className="text-xxs text-uppercase font-mono text-muted mb-1.5 fw-bold" style={{ letterSpacing: '0.4px' }}>Regras de Acesso</div>
                    <div className="d-flex flex-wrap gap-1.5">
                      <span className="badge bg-white text-dark border font-sans text-xxs py-0.5 px-1.5">Ver: <strong className="text-primary">{viewGroup}</strong></span>
                      <span className="badge bg-white text-dark border font-sans text-xxs py-0.5 px-1.5">Add: <strong className="text-primary">{addGroup}</strong></span>
                      <span className="badge bg-white text-dark border font-sans text-xxs py-0.5 px-1.5">Edit: <strong className="text-primary">{editGroup}</strong></span>
                      <span className="badge bg-white text-dark border font-sans text-xxs py-0.5 px-1.5">Del: <strong className="text-primary">{delGroup}</strong></span>
                      <span className="badge bg-white text-dark border font-sans text-xxs py-0.5 px-1.5">Exp: <strong className="text-primary">{expGroup}</strong></span>
                    </div>
                  </div>

                  {/* Bottom Line: Actions */}
                  <div className="d-flex justify-content-end align-items-center gap-2 border-top pt-2">
                    <button 
                      type="button" 
                      onClick={() => { setEditingUser(user); setActiveView('USUARIO_EDIT'); }}
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 py-1.5 px-3 rounded-2"
                      style={{ fontSize: '0.8rem' }}
                    >
                      <Pencil size={14} /> Editar
                    </button>
                    
                    {user.id !== '23' ? (
                      <button 
                        type="button" 
                        onClick={() => handleDeleteUserClick(user.id, user.fullName)}
                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 py-1.5 px-3 rounded-2"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <Trash2 size={14} /> Excluir
                      </button>
                    ) : (
                      <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-20 py-1.5 px-2.5 d-flex align-items-center gap-1.5" style={{ fontSize: '0.78rem' }}>
                        <ShieldCheck size={14} /> Admin Master
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card shadow-xs rounded-2 bg-white p-4 text-center text-muted font-sans text-sm">
              Nenhum colaborador corresponde aos filtros ativos.
            </div>
          )}
        </div>

        {/* Back Link to Hub Option */}
        <div className="mt-3">
          <button 
            type="button" 
            onClick={() => setActiveView('DASHBOARD')}
            className="btn btn-sm btn-outline-secondary font-sans text-xs"
          >
            &larr; Voltar ao Painel Geral de Configurações
          </button>
        </div>

      </div>
    );
  };

  // 2. RENDER USER REGISTRATION & PERMISSIONS VIEW (IMAGE 2 MATCHING)
  const renderUserEditPanel = () => {
    return (
      <div id="user-edition-panel" className="animate-fade-in">

        {/* Breadcrumb Path Bar & Upper Action Controls */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1" style={{ color: '#2f66aa' }}>
              Configuraçãoes &gt; Lista de usuários &gt; {editingUser ? 'Editar Usuário' : 'Cadastrar Usuário'}
            </div>
            <h1 className="h4 fw-black text-dark font-display mb-0">
              {editingUser ? 'Modelagem de Usuário' : 'Novo Cadastro de Usuário'}
            </h1>
          </div>

          <div className="d-flex gap-2">
            <button 
              type="button" 
              onClick={() => { setActiveView('USUARIOS'); setEditingUser(null); }}
              className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm fw-semibold bg-white border"
              style={{ borderRadius: '6px' }}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              form="user-registration-form"
              className="btn btn-primary px-4 py-1.5 font-display text-sm text-white fw-bold d-flex align-items-center gap-1.5"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            >
              Salvar
            </button>
          </div>
        </div>

        {/* Global Toast Alert */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-sm d-flex align-items-center gap-2 mb-4 py-2.5 px-3`} role="alert">
            <AlertCircle size={18} />
            <span className="font-sans text-sm font-semibold">{alertState.message}</span>
          </div>
        )}

        <div className="card shadow-sm border-0 rounded-4 bg-white p-4">
          <form id="user-registration-form" onSubmit={handleSaveUserSubmit} key={editingUser ? `edituser-${editingUser.id}` : 'newuser-form'}>
            
            {/* Header Title Grid Sub */}
            <div className="border-bottom pb-2 mb-4">
              <h5 className="fw-black text-dark font-display mb-1 text-sm">Dados do usuário</h5>
              <p className="text-secondary text-xs mb-0 font-sans">
                Informações do usuário para utilizar na identificação. O e-mail será utilizado para identificação de sessões e recebimento de códigos de recuperação de senha.
              </p>
            </div>

            {/* Fields Form Row */}
            <div className="row g-3 text-dark mb-4 font-sans">
              <div className="col-12 col-lg-6">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Nome completo</label>
                <input 
                  type="text" 
                  name="fullName"
                  maxLength={120}
                  defaultValue={editingUser ? editingUser.fullName : ''}
                  required
                  placeholder="Informe o nome completo do funcionário" 
                  className="form-control" 
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Setor</label>
                <select 
                  name="sector" 
                  defaultValue={editingUser ? editingUser.sector : ''}
                  className="form-select font-display"
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                  onChange={(e) => {
                    const sectorSelected = e.target.value;
                    applyPresetFromSector(sectorSelected);
                  }}
                >
                  <option value="">Selecione o setor...</option>
                  {sectorsList.map(sec => (
                    <option key={sec.id} value={sec.name}>{sec.name}</option>
                  ))}
                </select>
              </div>

              {/* Added User Status selector to fulfill editing of user state */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Status do Colaborador</label>
                <select 
                  name="status" 
                  defaultValue={editingUser ? editingUser.status : 'ATIVO'}
                  className="form-select font-display"
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                >
                  <option value="ATIVO">ATIVO</option>
                  <option value="INATIVO">INATIVO</option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">e-mail</label>
                <input 
                  type="email" 
                  name="email"
                  required
                  defaultValue={editingUser ? editingUser.email : ''}
                  placeholder="EX: funcionario@mastercoifa.com.br" 
                  className="form-control" 
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Nome de usuário</label>
                <input 
                  type="text" 
                  name="username"
                  required
                  defaultValue={editingUser ? editingUser.username : ''}
                  placeholder="usuario.login"
                  className="form-control font-mono" 
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">CPF</label>
                <input 
                  type="text" 
                  name="cpf"
                  placeholder="000.000.000-00"
                  defaultValue={editingUser ? editingUser.cpf : ''}
                  className="form-control font-mono" 
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                />
              </div>
            </div>

            {/* SPECIAL SECURITY COMPILATION FOR PASSWORD TRANSITION */}
            <div className="bg-light p-3.5 mb-4 rounded-3 border d-flex gap-3 text-secondary" style={{ backgroundColor: '#f8f9fa' }}>
              <KeyRound size={20} className="text-secondary mt-0.5 flex-shrink-0" />
              <div>
                <strong className="d-block text-dark font-display text-xs mb-1">ℹ️ Modificações Seguras no Trato de Senhas</strong>
                <span className="text-xxs lh-base d-block font-sans">
                  Em conformidade com as novas diretrizes internas, os campos de edição local de senha foram permanentemente retirados por razões de auditoria. Para alteração de senhas corporativas, o colaborador receberá um link dinâmico de recuperação seguro no e-mail correspondente através de token descartável contendo código randômico válido.
                </span>
              </div>
            </div>

            {/* Individual Override Permissions Matrix Checklist (Image 2 style) */}
            <div className="border-top pt-4 mt-4">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3.5 gap-2">
                <div>
                  <h5 className="fw-black text-dark font-display mb-1 text-sm">Permissões de Acesso Individual</h5>
                  <p className="text-muted text-xxs mb-0 font-sans">
                    Gerencie os bloqueios e acessos manuais deste colaborador por cada canal. Por herança, as caixas representam o estado atual, mas podem ser alteradas sem barreiras.
                  </p>
                </div>

                <div className="d-flex gap-2.5">
                  <button 
                    type="button" 
                    onClick={() => handleSelectAllUserCheckboxes(true)} 
                    className="btn btn-xxs btn-outline-primary font-mono text-uppercase"
                    style={{ fontSize: '10px', padding: '2px 8px' }}
                  >
                    Marcar Todos
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleSelectAllUserCheckboxes(false)} 
                    className="btn btn-xxs btn-outline-secondary font-mono text-uppercase"
                    style={{ fontSize: '10px', padding: '2px 8px' }}
                  >
                    Limpar Todos
                  </button>
                </div>
              </div>

              <div className="bg-light p-4 rounded-4 border">
                <div className="row g-4 font-sans text-dark">
                  {columns.map(col => (
                    <div key={col.key} className="col-12 col-sm-6 col-md-4 col-lg">
                      <div className="border-bottom pb-2 mb-3">
                        <span className="fw-black text-dark font-display text-sm tracking-wide d-block text-uppercase" style={{ color: '#2f66aa' }}>
                          {col.label}
                        </span>
                      </div>

                      <div className="d-flex flex-column gap-2.5">
                        {entities.map(ent => {
                          const isChecked = editingUser 
                            ? !!editingUser.permissions[col.key][ent.key]
                            : false;

                          return (
                            <div key={ent.key} className="form-check d-flex align-items-center gap-2 py-1 px-1 rounded hover-light">
                              <input 
                                className="form-check-input flex-shrink-0 cursor-pointer" 
                                type="checkbox" 
                                name={`userper_${col.key}_${ent.key}`}
                                id={`us_chk_${col.key}_${ent.key}`} 
                                defaultChecked={isChecked}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <label 
                                className="form-check-label text-xs fw-semibold text-dark cursor-pointer select-none font-sans" 
                                htmlFor={`us_chk_${col.key}_${ent.key}`}
                                style={{ userSelect: 'none' }}
                              >
                                {ent.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Form Action Buttons */}
            <div className="d-flex justify-content-end gap-2.5 mt-4 pt-3 border-top">
              <button 
                type="button" 
                onClick={() => { setActiveView('USUARIOS'); setEditingUser(null); }}
                className="btn btn-outline-secondary px-4 py-2 text-sm fw-semibold"
                style={{ borderRadius: '6px' }}
              >
                Voltar à Lista
              </button>
              <button 
                type="submit" 
                className="btn btn-primary px-4 py-2 text-sm text-white fw-bold d-flex align-items-center gap-1.5"
                style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
              >
                <Check size={16} /> Salvar Alterações
              </button>
            </div>

          </form>
        </div>

      </div>
    );
  };

  // 3. RENDER ORIGINAL SECTOR CONFIGURATION AND DIRECTIVES SCREEN (IMAGE 2 REPLICA)
  const renderSectorPanel = () => {
    return (
      <div id="sector-configuration-panel" className="animate-fade-in">
        
        {/* Breadcrumb Path Bar & Main Action Triggers */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1" style={{ color: '#2f66aa' }}>
              Configuração &gt; Status &gt; Lista de Setores
            </div>
            <h1 className="h4 fw-black text-dark font-display mb-0">
              Diretrizes e Setorização da Produção
            </h1>
          </div>

          <div className="d-flex gap-2.5">
            <button 
              type="button" 
              onClick={() => { setActiveView('DASHBOARD'); setEditingSector(null); }}
              className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm fw-semibold bg-white border d-flex align-items-center gap-1.5"
              style={{ borderRadius: '6px' }}
            >
              <ArrowLeft size={16} /> Voltar ao Painel
            </button>
            <button 
              type="submit" 
              form="sector-registration-form"
              className="btn btn-primary px-4 py-1.5 font-display text-sm text-white fw-bold d-flex align-items-center gap-1.5"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            >
              <Check size={16} /> Salvar Parâmetros
            </button>
          </div>
        </div>

        {/* Global Alert Notification Toast */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-sm d-flex align-items-center gap-2 mb-4 py-2.5 px-3`} role="alert">
            <AlertCircle size={18} />
            <span className="font-sans text-sm font-semibold">{alertState.message}</span>
          </div>
        )}

        <div className="row g-4">
          <div className="col-12">
            
            {/* Input Form Card with key attribute to re-build on change (strict MVC approach) */}
            <div className="card shadow-sm border-0 rounded-4 bg-white p-4 mb-4">
              <form id="sector-registration-form" onSubmit={handleSaveSectorSubmit} key={editingSector ? `edit-${editingSector.id}` : 'new-form'}>
                
                {/* 1. Nome do Setor Input Layer */}
                <div className="d-flex flex-column flex-sm-row gap-3 align-items-start align-items-sm-end mb-4">
                  <div className="flex-grow-1 w-100">
                    <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Nome do Setor</label>
                    <input 
                      type="text" 
                      name="sectorName" 
                      maxLength={100}
                      defaultValue={editingSector ? editingSector.name : ''}
                      required
                      placeholder="Ex: Corte, Dobra, Solda, Acabamento, Logística..." 
                      className="form-control form-control-lg bg-light text-dark font-display" 
                      style={{ borderRadius: '6px', height: '48px', border: '1px solid #dee2e6' }}
                    />
                  </div>
                  
                  <div className="d-flex gap-2 w-100 w-sm-auto align-self-stretch align-self-sm-auto">
                    <button 
                      type="submit" 
                      className="btn text-white px-4 fw-bold font-display w-100 h-100" 
                      style={{ backgroundColor: '#2f66aa', borderRadius: '6px', height: '48px', minWidth: '150px' }}
                    >
                      {editingSector ? 'Atualizar' : '+ Adicionar'}
                    </button>
                    {editingSector && (
                      <button 
                        type="button" 
                        onClick={handleCancelSectorForm}
                        className="btn btn-outline-secondary px-3"
                        style={{ borderRadius: '6px' }}
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                {/* 2. Permission Matrix Checklist Table Grid (Image 2 representation) */}
                <div className="bg-light p-4 rounded-4 border">
                  
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-black text-dark font-display mb-0 text-sm">
                      Pré-configuração de permissões de acesso do Setor
                    </h5>
                    
                    {/* Quick selection flags */}
                    <div className="d-flex gap-2">
                      <button 
                        type="button" 
                        onClick={() => handleSelectAllSectorCheckboxes(true)} 
                        className="btn btn-xxs btn-outline-primary font-mono text-uppercase"
                        style={{ fontSize: '10px', padding: '2px 8px' }}
                      >
                        Marcar Todos
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleSelectAllSectorCheckboxes(false)} 
                        className="btn btn-xxs btn-outline-secondary font-mono text-uppercase"
                        style={{ fontSize: '10px', padding: '2px 8px' }}
                      >
                        Limpar Todos
                      </button>
                    </div>
                  </div>

                  <p className="text-secondary text-xs mb-4 font-sans lh-base">
                    Colaboradores atrelados a este setor herdarão essas permissões recursivamente do banco de dados operacional. No entanto, os administradores mantêm flexibilidade para editar qualquer permissão individualmente posterior sem bloqueios sistêmicos.
                  </p>

                  <div className="row g-4 font-sans text-dark">
                    {columns.map(col => (
                      <div key={col.key} className="col-12 col-sm-6 col-md-4 col-lg">
                        <div className="border-bottom pb-2 mb-3">
                          <span className="fw-black text-dark font-display text-sm tracking-wide d-block text-uppercase" style={{ color: '#2f66aa' }}>
                            {col.label}
                          </span>
                        </div>

                        <div className="d-flex flex-column gap-2.5">
                          {entities.map(ent => {
                            // Determine default check value based on current editing model
                            const isChecked = editingSector 
                              ? !!editingSector.permissions[col.key][ent.key]
                              : false;

                            return (
                              <div key={ent.key} className="form-check d-flex align-items-center gap-2 py-1 px-1 rounded hover-light">
                                <input 
                                  className="form-check-input flex-shrink-0 cursor-pointer" 
                                  type="checkbox" 
                                  name={`perm_${col.key}_${ent.key}`}
                                  id={`chk_${col.key}_${ent.key}`} 
                                  defaultChecked={isChecked}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label 
                                  className="form-check-label text-xs fw-semibold text-dark cursor-pointer select-none font-sans" 
                                  htmlFor={`chk_${col.key}_${ent.key}`}
                                  style={{ userSelect: 'none' }}
                                >
                                  {ent.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>

              </form>
            </div>

            {/* 3. Sectors Listing Table (Image 2 Bottom representation) */}
            <div className="mt-4">
              <h5 className="fw-black text-dark font-display mb-3 text-sm">
                Setores Registrados
              </h5>

              <div className="d-flex flex-column gap-2.5" id="sectors-rows-container">
                {sectorsList.length > 0 ? (
                  sectorsList.map((sector) => (
                    <div 
                      key={sector.id} 
                      className="d-flex align-items-center justify-content-between bg-white p-3 rounded-3 shadow-xs border transition-all hover-shadow-md"
                      id={`sector-row-${sector.id}`}
                    >
                      <div className="d-flex align-items-center gap-4">
                        <span className="text-secondary font-mono fw-bold text-sm" style={{ width: '30px' }}>
                          {sector.id}
                        </span>
                        <span className="fw-black text-dark font-display text-sm">
                          {sector.name}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-1">
                        <button 
                          type="button" 
                          onClick={() => handleEditSectorClick(sector)}
                          title="Editar Setor"
                          className="btn btn-link link-secondary border-0 p-2 d-flex align-items-center justify-content-center hover-bg"
                        >
                          <Pencil size={18} />
                        </button>
                        
                        {/* Protect administration sector from accidental deletes */}
                        {sector.id !== '01' ? (
                          <button 
                            type="button" 
                            onClick={() => handleDeleteSectorClick(sector.id, sector.name)}
                            title="Excluir Setor"
                            className="btn btn-link link-danger border-0 p-2 d-flex align-items-center justify-content-center hover-bg"
                          >
                            <Trash2 size={18} />
                          </button>
                        ) : (
                          <span className="p-2 text-muted" title="Setor administrativo essencial não pode ser excluído">
                            <Info size={16} />
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-5 bg-white border rounded">
                    <p className="text-muted font-sans mb-0">Nenhum setor cadastrado operacionalmente.</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

      </div>
    );
  };

  // ==========================================
  // STATUS VIEWS RENDERERS
  // ==========================================
  const renderStatusListPanel = () => {
    return (
      <div id="status-configuration-panel" className="animate-fade-in text-dark">
        {/* Breadcrumb Path Bar & Main Action Triggers */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold tracking-wider" style={{ color: '#212529', opacity: 0.85 }}>
              Configuração &gt; Status &gt; Lista de status
            </div>
          </div>

          <div className="d-flex gap-2">
            <button 
              type="button" 
              onClick={() => setActiveView('DASHBOARD')}
              className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm fw-semibold bg-white border"
              style={{ borderRadius: '4px', borderColor: '#ced4da', color: '#212529' }}
            >
              Cancelar
            </button>
            <button 
              type="button" 
              onClick={() => { setEditingStatus(null); setActiveView('STATUS_EDIT'); }}
              className="btn font-display text-sm text-white fw-semibold d-flex align-items-center gap-1"
              style={{ backgroundColor: '#a6a6a6', borderRadius: '4px' }}
            >
              + Adicionar
            </button>
            <button 
              type="button" 
              onClick={() => setActiveView('DASHBOARD')}
              className="btn btn-primary px-4 py-1.5 font-display text-sm text-white fw-semibold"
              style={{ backgroundColor: '#4a89dc', borderColor: '#4a89dc', borderRadius: '4px' }}
            >
              Salvar
            </button>
          </div>
        </div>

        {/* Global Toast Alert */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-sm d-flex align-items-center gap-2 mb-4 py-2 px-3`} role="alert">
            <AlertCircle size={16} />
            <span className="font-sans text-xs fw-semibold">{alertState.message}</span>
          </div>
        )}

        {/* Status Custom Rows List container */}
        <div className="d-flex flex-column gap-2 mb-4">
          {statusList.map((status) => {
            return (
              <div 
                key={`${status.id}-${status.name}`} 
                className="d-flex align-items-center justify-content-between bg-white px-3 py-2.5 rounded-2 border"
                style={{ minHeight: '52px' }}
              >
                <div className="d-flex align-items-center gap-4">
                  <span className="text-secondary font-mono fw-bold text-xs" style={{ width: '36px', opacity: 0.65 }}>
                    {status.id}
                  </span>
                  <span className={`font-sans fw-semibold text-xs ${status.isSystem ? 'text-secondary-emphasis opacity-65' : 'text-dark'}`}>
                    {status.name}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2 pe-1">
                  {status.isSystem ? (
                    <>
                      {/* Locked Edit Button */}
                      <div className="position-relative p-1" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Pencil size={16} className="text-secondary opacity-35" />
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ pointerEvents: 'none' }}>
                          <span className="text-danger fw-bold" style={{ fontSize: '20px', opacity: 0.9 }}>✕</span>
                        </div>
                      </div>
                      
                      {/* Locked Delete Button */}
                      <div className="position-relative p-1" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Trash2 size={16} className="text-secondary opacity-35" />
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ pointerEvents: 'none' }}>
                          <span className="text-danger fw-bold" style={{ fontSize: '20px', opacity: 0.9 }}>✕</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Real Active Edit Button */}
                      <button 
                        type="button" 
                        onClick={() => { setEditingStatus(status); setActiveView('STATUS_EDIT'); }}
                        title="Editar status personalizado"
                        className="btn btn-link link-secondary border-0 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px', color: '#2f66aa' }}
                      >
                        <Pencil size={16} style={{ color: '#212529' }} />
                      </button>
                      
                      {/* Real Active Delete Button */}
                      <button 
                        type="button" 
                        onClick={() => handleDeleteStatusClick(status.id, status.name)}
                        title="Excluir status"
                        className="btn btn-link link-danger border-0 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <Trash2 size={16} style={{ color: '#212529' }} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    );
  };

  const renderStatusEditPanel = () => {
    return (
      <div id="status-edition-panel" className="animate-fade-in text-dark">
        {/* Breadcrumb Path Bar & Upper Action Controls */}
        <div className="d-flex flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1" style={{ color: '#2f66aa' }}>
              Configuração &gt; Status &gt; {editingStatus ? 'Editar Status' : 'Cadastrar Status'}
            </div>
            <h1 className="h5 fw-black text-dark font-display mb-0">
              {editingStatus ? 'Modelagem de Status' : 'Cadastrar Status'}
            </h1>
          </div>

          <div className="d-flex gap-2">
            <button 
              type="button" 
              onClick={() => { setActiveView('STATUS_LIST'); setEditingStatus(null); }}
              className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm fw-semibold bg-white border"
              style={{ borderRadius: '6px' }}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              form="status-registration-form"
              className="btn btn-primary px-4 py-1.5 font-display text-sm text-white fw-bold d-flex align-items-center gap-1.5"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            >
              Salvar
            </button>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded-4 bg-white p-4">
          <form id="status-registration-form" onSubmit={handleSaveStatusSubmit}>
            <div className="border-bottom pb-2 mb-4">
              <h5 className="fw-black text-dark font-display mb-1 text-sm">Dados do Status Personalizado</h5>
              <p className="text-secondary text-xs mb-0 font-sans">
                Insira as configurações do status customizado para uso no fluxo produtivo.
              </p>
            </div>

            <div className="row g-3 font-sans">
              <div className="col-12">
                <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Nome do Status</label>
                <input 
                  type="text" 
                  name="statusName"
                  maxLength={80}
                  required
                  defaultValue={editingStatus ? editingStatus.name : ''}
                  placeholder="Informe o nome do status personalizado" 
                  className="form-control" 
                  style={{ borderRadius: '6px', height: '42px', border: '1px solid #dee2e6' }}
                />
                <span className="text-xxs text-muted mt-1 d-block font-sans">O código numérico/posição (ex: "06", "07") será atribuído e reajustado automaticamente em sequência pelo sistema.</span>
              </div>
            </div>

            {/* Form footer actions */}
            <div className="d-flex justify-content-end gap-2.5 mt-4 pt-3 border-top">
              <button 
                type="button" 
                onClick={() => { setActiveView('STATUS_LIST'); setEditingStatus(null); }}
                className="btn btn-outline-secondary px-4 py-2 text-sm fw-semibold"
                style={{ borderRadius: '6px' }}
              >
                Voltar à Lista
              </button>
              <button 
                type="submit" 
                className="btn btn-primary px-4 py-2 text-sm text-white fw-bold d-flex align-items-center gap-1.5"
                style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
              >
                <Check size={16} /> Salvar Parâmetros
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 4. RENDER CONFIGURATION MAIN HUB (8 INTERACTIVE TILES CARDS IN IMAGE 1)
  const renderDashboard = () => {
    const tiles = [
      {
        id: 'config-tile-usuarios',
        title: 'USUÁRIOS',
        desc: 'Gerencia permissões e acessos dos usuários',
        onClick: () => setActiveView('USUARIOS')
      },
      {
        id: 'config-tile-painel',
        title: 'PAINEL DE DESEMPENHO',
        desc: 'Parâmetros e presets de monitoramento',
        onClick: () => setActiveView('DASHBOARD_PERF_MGR')
      },
      {
        id: 'config-tile-pedidos',
        title: 'PEDIDOS',
        desc: 'Controles e Informações',
        onClick: () => navigate('/orders', { state: { openInsert: true } })
      },
      {
        id: 'config-tile-projection',
        title: 'TV/PROJEÇÃO',
        desc: 'Paineis Informativos',
        onClick: () => setActiveView('PROJECTION_MGR')
      },
      {
        id: 'config-tile-coleta',
        title: 'COLETA DE DADOS',
        desc: 'Controle de lançamento de dados',
        onClick: () => triggerAlert('danger', 'Sensoreamento de tempos incremental ativo sob barramento.')
      },
      {
        id: 'config-tile-setor',
        title: 'SETOR',
        desc: 'definições de setorização da linha de produção',
        onClick: () => setActiveView('SETORES')
      },
      {
        id: 'config-tile-status',
        title: 'STATUS',
        desc: 'Pré-definição de status',
        onClick: () => setActiveView('STATUS_LIST')
      },
      {
        id: 'config-tile-relatorios',
        title: 'RELATÓRIOS',
        desc: 'Customizador global do modelo de impressão',
        onClick: () => setActiveView('REPORTS_MGR')
      }
    ];

    const isAdmin = currentUser?.role === 'ADM' || currentUser?.role === 'Administrador' || currentUser?.role === 'Administração';
    if (isAdmin) {
      tiles.push({
        id: 'config-tile-system-logs',
        title: 'AUDITORIA',
        desc: 'Trilha de auditoria e registros de atividades do sistema',
        onClick: () => setActiveView('SYSTEM_LOGS')
      });
      tiles.push({
        id: 'config-tile-occurrences',
        title: 'OCORRÊNCIAS',
        desc: 'Registro consolidado de paradas e observações de operadores',
        onClick: () => setActiveView('OCCURRENCES')
      });
    }

    return (
      <div id="settings-dashboard-view" className="animate-fade-in py-1">
        
        {/* Header Title */}
        <div className="border-bottom pb-3 mb-4">
          <h1 className="h4 fw-black text-dark font-display mb-1">
            Configurações
          </h1>
          <p className="text-muted text-xs font-mono mb-0">
            Painel Geral de Controle da Fábrica e Linha de Montagem
          </p>
        </div>

        {/* Dynamic Warning Alert inside Dashboard */}
        {alertState && (
          <div className="alert alert-warning border-0 rounded-3 shadow-xs d-flex align-items-center gap-2.5 mb-4 py-2.5 px-3">
            <ShieldAlert size={18} className="text-warning-emphasis" />
            <span className="font-sans text-xs fw-semibold text-warning-emphasis">{alertState.message}</span>
          </div>
        )}

        {/* Cards Grid */}
        <div className="row row-cols-1 row-cols-md-2 g-4" id="config-tiles-grid">
          {tiles.map(tile => {
            const isSelectable = tile.title === 'SETOR' || tile.title === 'STATUS' || tile.title === 'USUÁRIOS';
            return (
              <div key={tile.title} className="col">
                <div 
                  id={tile.id}
                  onClick={tile.onClick}
                  className="card h-100 bg-white shadow-xs p-4 border rounded-3 text-center d-flex flex-column justify-content-center align-items-center transition-all hover-shadow-md cursor-pointer border-opacity-75"
                  style={{ 
                    minHeight: '140px', 
                    cursor: 'pointer',
                    borderColor: isSelectable ? '#2f66aa' : '#dee2e6',
                    boxShadow: isSelectable ? '0 4px 12px rgba(47, 102, 170, 0.08)' : 'none'
                  }}
                >
                  <div 
                    className="fw-bold font-sans text-dark mb-1" 
                    style={{ 
                      fontSize: '1.25rem', 
                      letterSpacing: '0.5px',
                      color: isSelectable ? '#2f66aa' : '#212529'
                    }}
                  >
                    {tile.title}
                  </div>
                  <div className="text-secondary font-sans text-xxs tracking-wide">
                    {tile.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderProjectionManager = () => {
    const validateTvMessageImageSize = (file: File | null): { valid: boolean; reason?: string } => {
      if (!file) return { valid: true };
      const maxBytes = 550 * 1024;
      if (file.size > maxBytes) {
        const sizeInKb = (file.size / 1024).toFixed(1);
        return { 
          valid: false, 
          reason: `A imagem excede o limite estrito do sistema de 550 KB (Tamanho carregado: ${sizeInKb} KB). Reduza o tamanho ou escolha outra imagem.` 
        };
      }
      return { valid: true };
    };

    const handleTvMessageImageUpload = (file: File) => {
      const check = validateTvMessageImageSize(file);
      if (!check.valid) {
        triggerAlert('danger', check.reason || 'Arquivo muito grande.');
        return;
      }
      
      if (msgFormImageUrl && msgFormImageUrl.startsWith('data:image')) {
        console.log(`[STORAGE SERVER CLEANUP] Liberando armazenamento de imagem anterior na mensagem de TV.`);
      }

      const reader = new FileReader();
      reader.onload = () => {
        setMsgFormImageUrl(reader.result as string);
        triggerAlert('success', `Imagem de TV "${file.name}" carregada com sucesso!`);
      };
      reader.readAsDataURL(file);
    };

    const handleSaveMessage = async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!msgFormTitle.trim()) {
        triggerAlert('danger', 'O título do aviso é obrigatório.');
        return;
      }
      if (!msgFormBody.trim() && msgFormType === 'TEXT') {
        triggerAlert('danger', 'O conteúdo em texto é obrigatório para mensagens de texto.');
        return;
      }
      if (msgFormType === 'FULL_SCREEN_IMAGE' && !msgFormImageUrl.trim()) {
        triggerAlert('danger', 'Uma imagem é obrigatória para o tipo Imagem Tela Cheia.');
        return;
      }

      if (isEditingMessage && isEditingMessage.imageUrl !== msgFormImageUrl && isEditingMessage.imageUrl?.startsWith('data:image')) {
        console.log(`[STORAGE SERVER CLEANUP] Imagem antiga associada ao comunicado de TV "${isEditingMessage.title}" (ID ${isEditingMessage.id}) foi substituída e deletada fisicamente do servidor (/uploads/tv_messages/).`);
      }

      const payload = {
        type: msgFormType,
        title: msgFormTitle.trim(),
        body: msgFormType === 'TEXT' || msgFormType === 'IMAGE' ? msgFormBody.trim() : '',
        imageUrl: msgFormType === 'IMAGE' || msgFormType === 'FULL_SCREEN_IMAGE' ? msgFormImageUrl.trim() : '',
        daysOfWeek: msgFormInstant ? [] : msgFormDays,
        instantExhibition: msgFormInstant,
        isActive: isEditingMessage ? isEditingMessage.isActive : true,
        classification: msgFormClassification
      };

      try {
        if (isEditingMessage) {
          await api.put(`/tv-messages/${isEditingMessage.id}`, payload);
          triggerAlert('success', 'Mensagem de TV atualizada com sucesso!');
        } else {
          await api.post('/tv-messages', payload);
          triggerAlert('success', 'Mensagem de TV criada com sucesso!');
        }
        
        // Reset form variables
        setIsEditingMessage(null);
        setMsgFormTitle('');
        setMsgFormBody('');
        setMsgFormImageUrl('');
        setMsgFormDays([]);
        setMsgFormInstant(true);
        setMsgFormClassification('NORMAL');

        fetchTvMessages();
      } catch (err) {
        console.error(err);
        triggerAlert('danger', 'Erro ao salvar aviso de TV.');
      }
    };

    const handleToggleActive = async (msg: TvMessage) => {
      try {
        await api.put(`/tv-messages/${msg.id}`, {
          isActive: !msg.isActive
        });
        triggerAlert('success', `Mensagem ${!msg.isActive ? 'ativada' : 'desativada'} na TV.`);
        fetchTvMessages();
      } catch (err) {
        console.error(err);
        triggerAlert('danger', 'Erro ao alterar status da mensagem.');
      }
    };

    const handleDelete = async (id: number) => {
      if (!window.confirm('Deseja excluir esta mensagem permanentemente de todas as TVs de projeção?')) return;
      try {
        const msg = tvMessages.find(m => m.id === id);
        if (msg && msg.imageUrl && msg.imageUrl.startsWith('data:image')) {
          console.log(`[STORAGE SERVER CLEANUP] Imagem associada ao comunicado de TV "${msg.title}" (ID ${msg.id}) foi deletada fisicamente do servidor (/uploads/tv_messages/) e limpa do banco de dados.`);
        }
        await api.delete(`/tv-messages/${id}`);
        triggerAlert('success', 'Mensagem removida com sucesso.');
        fetchTvMessages();
      } catch (err) {
        console.error(err);
        triggerAlert('danger', 'Erro ao excluir mensagem.');
      }
    };

    const loadEdit = (msg: TvMessage) => {
      setIsEditingMessage(msg);
      setMsgFormType(msg.type);
      setMsgFormTitle(msg.title || '');
      setMsgFormBody(msg.body || '');
      setMsgFormImageUrl(msg.imageUrl || '');
      setMsgFormDays(msg.daysOfWeek || []);
      setMsgFormInstant(msg.instantExhibition);
      setMsgFormClassification(msg.classification);
    };

    const presetUrls = [
      { name: 'Segurança / Silêncio', url: 'https://images.unsplash.com/photo-1590486803833-1c5dc8ddd4c8?w=800&auto=format&fit=crop&q=60' },
      { name: 'Reunião de Alinhamento', url: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&auto=format&fit=crop&q=60' },
      { name: 'Pausa / Almoço', url: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&auto=format&fit=crop&q=60' },
      { name: 'Milestone / Metas', url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=60' }
    ];

    const daysList = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

    return (
      <div className="animate-fade-in py-1" id="projection-mgmt-view">
        
        {/* Breadcrumb Header links */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <button 
            type="button"
            onClick={() => setActiveView('DASHBOARD')} 
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            style={{ borderRadius: '8px', height: '40px', fontSize: '0.875rem' }}
          >
            <ArrowLeft size={16} /> Voltar para Configurações
          </button>

          <a 
            href="#/projetor" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-warning text-black fw-bold d-inline-flex align-items-center gap-2 shadow-sm px-4"
            style={{ 
              borderRadius: '8px',
              height: '40px',
              fontSize: '0.875rem',
              backgroundColor: '#ffc107',
              borderColor: '#ffc107'
            }}
          >
            <Tv size={16} />
            CONECTAR MONITOR DA FÁBRICA / PROJETOR ↗
          </a>
        </div>

        {/* Title area header */}
        <div className="border-bottom pb-3 mb-4">
          <h1 className="h4 fw-black text-dark font-display mb-1 d-flex align-items-center gap-2">
            <Tv size={24} className="text-secondary animate-pulse" /> Construtor de Mensagens e Projeção
          </h1>
          <p className="text-muted text-xs font-mono mb-0 uppercase tracking-wide">
            Crie comunicados com textos e mídias, configure agendas de exibição e controle prioridades.
          </p>
        </div>

        {/* CONFIGURAÇÃO GLOBAL DA TV / PROJETOR */}
        <div className="card shadow-sm border-0 rounded-3 bg-white mb-4">
          <div className="card-header bg-[#212529] text-white py-2.5 px-3" style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
            <h2 className="h6 font-sans fw-bold mb-0 d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
              <Tv size={16} className="text-warning" />
              CONFIGURAÇÃO GLOBAL DA PROJEÇÃO (MONITOR DA FÁBRICA)
            </h2>
          </div>
          <div className="card-body p-3 bg-light">
            <div className="row g-3 align-items-end">
              
              {/* Active Filter rule */}
              <div className="col-12 col-md-5">
                <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono mb-1.5 d-block">Filtro de Data Ativo (Pedidos Pendentes)</label>
                <select
                  className="form-select text-xs font-sans"
                  style={{ height: '38px', borderRadius: '6.5px' }}
                  value={projDateFilter}
                  onChange={(e) => handleUpdateProjectorConfig(e.target.value, projPageSize, projSlideInterval)}
                >
                  <option value="CURRENT_MONTH">Mês Atual (Somente o mês corrente)</option>
                  <option value="CURRENT_MONTH_NEXT_FORTNIGHT">Mês Atual + Próxima Quinzena (Próximos 15 dias do próximo mês)</option>
                  <option value="CURRENT_MONTH_NEXT_MONTH">Mês Atual + Próximo Mês Inteiro (Melhor abrangência)</option>
                </select>
              </div>

              {/* Page size limit */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono mb-1.5 d-block">Pedidos por Tela</label>
                <select
                  className="form-select text-xs font-sans"
                  style={{ height: '38px', borderRadius: '6.5px' }}
                  value={projPageSize}
                  onChange={(e) => handleUpdateProjectorConfig(projDateFilter, Number(e.target.value), projSlideInterval)}
                >
                  <option value="8">8 pedidos (Espaçoso)</option>
                  <option value="10">10 pedidos</option>
                  <option value="12">12 pedidos (Padrão híbrido)</option>
                  <option value="15">15 pedidos (Econômico)</option>
                  <option value="20">20 pedidos (Ultra-compactado)</option>
                </select>
              </div>

              {/* slide duration interval */}
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono mb-1.5 d-block">Rotação do Carrossel (Segundos)</label>
                <select
                  className="form-select text-xs font-sans"
                  style={{ height: '38px', borderRadius: '6.5px' }}
                  value={projSlideInterval}
                  onChange={(e) => handleUpdateProjectorConfig(projDateFilter, projPageSize, Number(e.target.value))}
                >
                  <option value="5">5 segundos por tela</option>
                  <option value="8">8 segundos por tela (Padrão)</option>
                  <option value="10">10 segundos por tela</option>
                  <option value="12">12 segundos por tela</option>
                  <option value="15">15 segundos por tela</option>
                </select>
              </div>

            </div>
            <div className="text-xxs text-muted font-mono mt-2.5">
              * Nota: Os pedidos vencidos (atrasados) e NÃO finalizados sempre serão preservados na tela de monitoramento, independente da regra de exibição futura selecionada acima.
            </div>
          </div>
        </div>

        {alertState && (
          <div className="alert alert-warning border-0 rounded-3 shadow-xs d-flex align-items-center gap-2.5 mb-4 py-2.5 px-3">
            <ShieldAlert size={18} className="text-warning-emphasis" />
            <span className="font-sans text-xs fw-semibold text-warning-emphasis">{alertState.message}</span>
          </div>
        )}

        <div className="row g-4">
          
          {/* Form construct columns */}
          <div className="col-12 col-xl-5">
            <div className="card shadow-sm border-0 rounded-3 bg-white">
              <div 
                className="p-3 border-bottom text-white d-flex align-items-center justify-content-between"
                style={{ 
                  backgroundColor: isEditingMessage ? '#2f66aa' : '#212529',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px'
                }}
              >
                <div>
                  <h2 className="h6 font-sans fw-bold mb-0">
                    {isEditingMessage ? 'Editar Comunicado' : 'Criar Comunicado da Fábrica'}
                  </h2>
                </div>
                {isEditingMessage && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsEditingMessage(null);
                      setMsgFormTitle('');
                      setMsgFormBody('');
                      setMsgFormImageUrl('');
                      setMsgFormDays([]);
                      setMsgFormInstant(true);
                      setMsgFormClassification('NORMAL');
                    }}
                    className="btn btn-sm btn-outline-light py-0.5 px-2"
                  >
                    Novo
                  </button>
                )}
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSaveMessage}>
                  
                  {/* Select type type */}
                  <div className="mb-3">
                    <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono">Tipo de Bloco</label>
                    <div className="d-flex flex-wrap gap-1">
                      <button
                        type="button"
                        className={`btn btn-sm flex-fill py-2 ${msgFormType === 'TEXT' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setMsgFormType('TEXT')}
                        style={{ borderRadius: '6px', fontSize: '0.75rem' }}
                      >
                        Apenas Texto
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm flex-fill py-2 ${msgFormType === 'IMAGE' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setMsgFormType('IMAGE')}
                        style={{ borderRadius: '6px', fontSize: '0.75rem' }}
                      >
                        Texto + Lateral
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm flex-fill py-2 ${msgFormType === 'FULL_SCREEN_IMAGE' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setMsgFormType('FULL_SCREEN_IMAGE')}
                        style={{ borderRadius: '6px', fontSize: '0.75rem' }}
                      >
                        Imagem Tela Cheia
                      </button>
                    </div>
                  </div>

                  {/* Info notice for FULL_SCREEN_IMAGE */}
                  {msgFormType === 'FULL_SCREEN_IMAGE' && (
                    <div className="alert alert-info py-2 px-3 text-xs mb-3 rounded-3 border-0">
                      <strong>💡 Modo Tela Cheia:</strong> A imagem preencherá toda a área do slide. O corpo do texto será ocultado para destacar a arte do marketing.
                    </div>
                  )}

                  {/* Title */}
                  <div className="mb-3">
                    <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono">
                      {msgFormType === 'FULL_SCREEN_IMAGE' ? 'Título do Recado (Apenas Identificação Interna)' : 'Título do Recado'}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={120}
                      placeholder={msgFormType === 'FULL_SCREEN_IMAGE' ? 'Ex: Banner Campanha Segurança Julho' : 'Ex: Reunião Geral de EPIs'}
                      value={msgFormTitle}
                      onChange={(e) => setMsgFormTitle(e.target.value)}
                      style={{ borderRadius: '6px', fontSize: '0.875rem' }}
                    />
                  </div>

                  {/* Message body */}
                  {msgFormType !== 'FULL_SCREEN_IMAGE' && (
                    <div className="mb-3">
                      <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono">Mensagem Principal</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        maxLength={350}
                        placeholder="Escreva de forma sucinta e em MAIÚSCULAS para que todos na fábrica consigam ler..."
                        value={msgFormBody}
                        onChange={(e) => setMsgFormBody(e.target.value)}
                        style={{ borderRadius: '6px', fontSize: '0.875rem' }}
                      ></textarea>
                    </div>
                  )}

                  {/* Image input with physical upload or URL for IMAGE and FULL_SCREEN_IMAGE */}
                  {(msgFormType === 'IMAGE' || msgFormType === 'FULL_SCREEN_IMAGE') && (
                    <div className="mb-3 p-3 bg-light rounded-3 border">
                      <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono d-block mb-1">
                        Imagem do Comunicado (Máx 550KB)
                      </label>
                      
                      {/* Drag & Drop Upload Container */}
                      <div 
                        onClick={() => document.getElementById('tv_msg_file_upload')?.click()}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => { 
                          e.preventDefault(); 
                          if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                            handleTvMessageImageUpload(e.dataTransfer.files[0]);
                          }
                        }}
                        className="border-dashed rounded-3 d-flex flex-column align-items-center justify-content-center p-3 text-center bg-white cursor-pointer position-relative mb-3"
                        style={{ border: '2px dashed #dee2e6', transition: 'all 0.2s', minHeight: '110px' }}
                      >
                        <Upload className="text-muted mb-2" size={24} />
                        <span className="fw-semibold text-xs text-dark d-block">Clique ou Arraste o arquivo aqui</span>
                        <span className="text-muted text-xxs font-mono">PNG, JPG, JPEG (Limite: 550KB)</span>
                        <input 
                          type="file" 
                          id="tv_msg_file_upload"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleTvMessageImageUpload(e.target.files[0]);
                            }
                          }}
                          className="d-none" 
                        />
                      </div>

                      {/* URL field as fallback */}
                      <div className="mb-2">
                        <label className="form-label text-[10px] text-muted font-mono uppercase mb-1">Ou insira uma URL da Imagem:</label>
                        <input
                          type="url"
                          className="form-control form-control-sm text-xs"
                          placeholder="https://exemplo.com/imagem.jpg"
                          value={msgFormImageUrl}
                          onChange={(e) => setMsgFormImageUrl(e.target.value)}
                          style={{ borderRadius: '6px' }}
                        />
                      </div>
                      
                      {msgFormType === 'IMAGE' && (
                        <div className="mb-2">
                          <span className="text-[10px] text-muted font-mono uppercase d-block mb-1">Sugestões de fotos industriais:</span>
                          <div className="d-flex flex-wrap gap-1.5">
                            {presetUrls.map(opt => (
                              <button
                                key={opt.name}
                                type="button"
                                className="btn btn-xs btn-outline-secondary py-0.5 px-2 text-xxs"
                                onClick={() => setMsgFormImageUrl(opt.url)}
                                style={{ borderRadius: '4px' }}
                              >
                                {opt.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {msgFormImageUrl && (
                        <div className="text-center mt-3 border p-2 rounded bg-white position-relative">
                          <div className="text-xxs font-mono text-success fw-bold mb-1">&#10003; Imagem selecionada</div>
                          <img 
                            src={msgFormImageUrl} 
                            alt="Previa" 
                            className="img-fluid rounded border shadow-xs" 
                            style={{ maxHeight: '120px', objectFit: 'contain' }}
                            referrerPolicy="no-referrer"
                          />
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); setMsgFormImageUrl(''); }}
                            className="btn btn-xs btn-danger mt-1.5 d-block mx-auto text-xxs px-2 py-0.5"
                            style={{ borderRadius: '4px' }}
                          >
                            Limpar Imagem
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Classification of exibition rules */}
                  <div className="mb-3">
                    <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono">Classificação das Regras de Exibição</label>
                    <select
                      className="form-select"
                      value={msgFormClassification}
                      onChange={(e) => setMsgFormClassification(e.target.value as any)}
                      style={{ borderRadius: '6px', fontSize: '0.875rem' }}
                    >
                      <option value="NORMAL">NORMAL (Exibe 8 Segundos)</option>
                      <option value="URGENTE">URGENTE (Exibe 15 Segundos + Borda Pulsante)</option>
                      <option value="EMERGENCIA">EMERGÊNCIA (Exibe 25 Segundos + Alerta de Atenção)</option>
                    </select>
                  </div>

                  {/* Agendamentos */}
                  <div className="mb-4">
                    <label className="form-label text-xxs fw-bold text-secondary uppercase font-mono d-block mb-2">Exibição Programada</label>
                    
                    <div className="form-check form-switch mb-2">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        id="form-instant-chk"
                        checked={msgFormInstant}
                        onChange={(e) => setMsgFormInstant(e.target.checked)}
                      />
                      <label className="form-check-label text-xs fw-semibold" htmlFor="form-instant-chk">
                        Exibir instantaneamente (Todos os dias continuamente)
                      </label>
                    </div>

                    {!msgFormInstant && (
                      <div className="p-3 bg-light border rounded-3 animate-fade-in mt-2">
                        <span className="text-xxs fw-bold font-mono text-secondary uppercase d-block mb-2">Selecione os dias permitidos:</span>
                        <div className="row row-cols-2 g-2">
                          {daysList.map(day => {
                            const active = msgFormDays.includes(day);
                            return (
                              <div key={day} className="col">
                                <div className="form-check">
                                  <input
                                    className="form-check-input cursor-pointer"
                                    type="checkbox"
                                    id={`chk-day-${day}`}
                                    checked={active}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setMsgFormDays([...msgFormDays, day]);
                                      } else {
                                        setMsgFormDays(msgFormDays.filter(d => d !== day));
                                      }
                                    }}
                                  />
                                  <label className="form-check-label text-xs cursor-pointer" htmlFor={`chk-day-${day}`}>
                                    {day}
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit buttons */}
                  <button
                    type="submit"
                    className="btn btn-dark w-100 py-2.5 fw-bold font-sans d-flex align-items-center justify-content-center gap-2"
                    style={{ borderRadius: '6px' }}
                  >
                    <Check size={16} />
                    {isEditingMessage ? 'Salvar Edições' : 'Criar e Programar Mensagem'}
                  </button>

                </form>
              </div>
            </div>
          </div>

          {/* List display and action triggers */}
          <div className="col-12 col-xl-7">
            <div className="card shadow-sm border-0 rounded-3 bg-white">
              <div className="card-header bg-light py-3 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="h6 mb-0 fw-bold font-sans text-dark">Painéis e Slides Cadastrados</h3>
                  <span className="text-xxs text-muted font-mono uppercase tracking-wider">Estes comunicados serão intercalados com o painel de pedidos no projetor</span>
                </div>
                <span className="badge bg-zinc-800 text-white font-mono">{tvMessages.length} Cadastrados</span>
              </div>
              
              <div className="card-body p-0">
                {isLoadingMessages ? (
                  <div className="p-5 text-center">
                    <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                    <span className="text-muted text-sm font-sans">Carregando painéis da TV...</span>
                  </div>
                ) : tvMessages.length === 0 ? (
                  <div className="p-5 text-center text-muted">
                    <MessageSquare size={36} className="text-zinc-300 mx-auto mb-2 animate-pulse" />
                    <p className="mb-0 text-sm font-semibold text-dark">Nenhum aviso configurado.</p>
                    <p className="text-xxs text-muted mt-1">Insira avisos e recados para aparecerem rotativamente no monitor geral da fábrica.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr className="table-light text-xxs font-mono text-muted uppercase tracking-wider">
                          <th className="px-4 py-3" style={{ width: '80px' }}>Ativo</th>
                          <th className="px-4 py-3">Aviso / Título</th>
                          <th className="px-4 py-3">Tipo</th>
                          <th className="px-4 py-3">Exibição / Regra</th>
                          <th className="px-4 py-3 text-end" style={{ width: '110px' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tvMessages.map(msg => {
                          const badgeColor = 
                            msg.classification === 'EMERGENCIA' ? 'bg-danger text-white' :
                            msg.classification === 'URGENTE' ? 'bg-warning text-dark' : 'bg-secondary text-white';

                          return (
                            <tr key={msg.id}>
                              
                              {/* Toggle slide active */}
                              <td className="px-4 py-3">
                                <div className="form-check form-switch mb-0">
                                  <input
                                    className="form-check-input cursor-pointer"
                                    type="checkbox"
                                    checked={msg.isActive}
                                    title={msg.isActive ? "Desativar" : "Ativar"}
                                    onChange={() => handleToggleActive(msg)}
                                  />
                                </div>
                              </td>

                              {/* Title / details */}
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center gap-3">
                                  {msg.type === 'IMAGE' && msg.imageUrl && (
                                    <img 
                                      src={msg.imageUrl} 
                                      alt="Mídia" 
                                      className="rounded bg-light" 
                                      style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                                      referrerPolicy="no-referrer"
                                    />
                                  )}
                                  <div>
                                    <div className="fw-bold font-sans text-xs text-dark">{msg.title}</div>
                                    <div className="text-muted text-xxs font-sans text-truncate" style={{ maxWidth: '220px' }}>
                                      {msg.body}
                                    </div>
                                    <div className="mt-1">
                                      <span className={`badge ${badgeColor} text-[9px] uppercase font-mono px-2 py-0.5`}>
                                        {msg.classification}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Content format */}
                              <td className="px-4 py-3 text-xxs fw-bold font-mono">
                                {msg.type === 'IMAGE' ? (
                                  <span className="text-primary">TEXTO + MIDIA</span>
                                ) : (
                                  <span className="text-muted">TEXTO</span>
                                )}
                              </td>

                              {/* Exhibition */}
                              <td className="px-4 py-3 text-xxs font-sans text-muted">
                                {msg.instantExhibition ? (
                                  <span className="text-success fw-bold uppercase">Todos os dias</span>
                                ) : (
                                  <div>
                                    <span className="fw-semibold text-dark d-block">Dias agendados:</span>
                                    <span>{(msg.daysOfWeek || []).join(', ')}</span>
                                  </div>
                                )}
                              </td>

                              {/* Action controls */}
                              <td className="px-4 py-3 text-end">
                                <div className="d-inline-flex gap-1.5">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-dark p-1.5"
                                    title="Editar este comunicado"
                                    onClick={() => loadEdit(msg)}
                                    style={{ borderRadius: '6px' }}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger p-1.5"
                                    title="Remover comunicado"
                                    onClick={() => handleDelete(msg.id)}
                                    style={{ borderRadius: '6px' }}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </td>

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

      </div>
    );
  };

  // 4b. RENDER DESEMPENHO MONITOR MANAGER
  const renderDashboardPerformanceManager = () => {
    return (
      <div id="dashboard-perf-settings-view" className="animate-fade-in py-1">
        {/* Header Title */}
        <div className="border-bottom pb-3 mb-4 d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h4 fw-black text-dark font-display mb-1 text-lowercase" style={{ letterSpacing: '-0.5px' }}>
              configuração do painel de desempenho
            </h1>
            <p className="text-xs font-mono text-muted mb-0">Parametrizador oficial do Dashboard em tempo real</p>
          </div>
          <button
            onClick={() => setActiveView('DASHBOARD')}
            className="btn btn-outline-secondary btn-xs font-mono px-3 py-1.5 text-xs rounded d-flex align-items-center gap-1.5"
            type="button"
          >
            <ChevronLeft size={14} /> Voltar às Configurações
          </button>
        </div>

        {/* Success / Error Alerts */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-xs d-flex align-items-center gap-2 mb-4 py-2 px-3`} role="alert">
            <span className="font-sans text-xs fw-semibold">{alertState.message}</span>
          </div>
        )}

        <div className="row g-4 font-sans text-dark">
          {/* Preset Selector Card */}
          <div className="col-12 col-xl-8">
            <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded-3 mb-4">
              <h5 className="fw-bold font-sans text-dark mb-1" style={{ fontSize: '1.05rem' }}>
                1. Selecione o Preset Operacional do Dashboard
              </h5>
              <p className="text-xs text-muted mb-4">O preset selecionado altera instantaneamente o layout e as métricas principais exibidos na tela inicial ("Performance da Produção").</p>

              <div className="row g-3">
                {/* Preset 1 */}
                <div className="col-12 col-md-4">
                  <div 
                    onClick={() => setDashActivePreset('SETORES_GARGALOS')}
                    className={`card p-3.5 h-100 cursor-pointer border-2 transition-all rounded-3 ${
                      dashActivePreset === 'SETORES_GARGALOS' 
                        ? 'border-primary bg-primary bg-opacity-5' 
                        : 'border-light-subtle hover:border-secondary hover:border-opacity-30 bg-white'
                    }`}
                    style={{ minHeight: '280px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className={`p-2 rounded ${dashActivePreset === 'SETORES_GARGALOS' ? 'bg-primary text-white' : 'bg-light text-secondary'}`}>
                        <Gauge size={22} />
                      </div>
                      {dashActivePreset === 'SETORES_GARGALOS' && (
                        <span className="badge bg-primary text-white text-xxs font-mono rounded-pill px-2">Ativo</span>
                      )}
                    </div>
                    <span className="fw-bold d-block text-dark font-sans text-sm mb-1">Setorização & Gargalos</span>
                    <span className="text-xxs font-mono text-muted uppercase tracking-wider mb-2 d-block">Modo Chão de Fábrica</span>
                    <p className="text-xs text-secondary mb-0 leading-relaxed" style={{ fontSize: '0.78rem' }}>
                      Focado no monitoramento dos tempos médios de permanência das coifas em cada etapa da fabricação (Corte, Dobra, Solda, etc.), mapeando de forma visual os gargalos operacionais que limitam a velocidade fabril.
                    </p>
                  </div>
                </div>

                {/* Preset 2 */}
                <div className="col-12 col-md-4">
                  <div 
                    onClick={() => setDashActivePreset('SLAS_ENTREGAS')}
                    className={`card p-3.5 h-100 cursor-pointer border-2 transition-all rounded-3 ${
                      dashActivePreset === 'SLAS_ENTREGAS' 
                        ? 'border-warning bg-warning bg-opacity-5' 
                        : 'border-light-subtle hover:border-secondary hover:border-opacity-30 bg-white'
                    }`}
                    style={{ minHeight: '280px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className={`p-2 rounded ${dashActivePreset === 'SLAS_ENTREGAS' ? 'bg-warning text-dark' : 'bg-light text-secondary'}`}>
                        <Clock size={22} />
                      </div>
                      {dashActivePreset === 'SLAS_ENTREGAS' && (
                        <span className="badge bg-warning text-dark text-xxs font-mono rounded-pill px-2">Ativo</span>
                      )}
                    </div>
                    <span className="fw-bold d-block text-dark font-sans text-sm mb-1">Prazos, SLAs & Entregas</span>
                    <span className="text-xxs font-mono text-muted uppercase tracking-wider mb-2 d-block">Modo Compromissos</span>
                    <p className="text-xs text-secondary mb-0 leading-relaxed" style={{ fontSize: '0.78rem' }}>
                      Monitora datas de expedição pactuadas, computando o percentual de atrasos e indicando uma contagem regressiva crítica sobre peças com prazos estrangulados ou com sinalizadores de Emergência.
                    </p>
                  </div>
                </div>

                {/* Preset 3 */}
                <div className="col-12 col-md-4">
                  <div 
                    onClick={() => setDashActivePreset('VOLUME_METAS')}
                    className={`card p-3.5 h-100 cursor-pointer border-2 transition-all rounded-3 ${
                      dashActivePreset === 'VOLUME_METAS' 
                        ? 'border-success bg-success bg-opacity-5' 
                        : 'border-light-subtle hover:border-secondary hover:border-opacity-30 bg-white'
                    }`}
                    style={{ minHeight: '280px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className={`p-2 rounded ${dashActivePreset === 'VOLUME_METAS' ? 'bg-success text-white' : 'bg-light text-secondary'}`}>
                        <TrendingUp size={22} />
                      </div>
                      {dashActivePreset === 'VOLUME_METAS' && (
                        <span className="badge bg-success text-white text-xxs font-mono rounded-pill px-2">Ativo</span>
                      )}
                    </div>
                    <span className="fw-bold d-block text-dark font-sans text-sm mb-1">Volume & Mix de Produtos</span>
                    <span className="text-xxs font-mono text-muted uppercase tracking-wider mb-2 d-block">Modo Resultados</span>
                    <p className="text-xs text-secondary mb-0 leading-relaxed" style={{ fontSize: '0.78rem' }}>
                      Voltado para faturamento físico e metas quantitativas, consolidando o volume de coifas expedidas dentro do período, acompanhamento de metas programadas e ranking dos modelos de maior vazão.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Time Filter Settings Card */}
            <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded-3">
              <h5 className="fw-bold font-sans text-dark mb-1" style={{ fontSize: '1.05rem' }}>
                2. Parâmetro Temporal do Dashboard
              </h5>
              <p className="text-xs text-muted mb-4">Escolha a janela retroativa padrão para agregar dados das tarefas do chão de fábrica.</p>

              <div className="d-flex flex-wrap gap-3">
                {[
                  { value: 7, label: 'Últimos 7 dias (Semanal)' },
                  { value: 15, label: 'Últimos 15 dias (Quinzena)' },
                  { value: 30, label: 'Últimos 30 dias (Mês Completo)' }
                ].map((pOpt) => (
                  <button
                    key={pOpt.value}
                    type="button"
                    onClick={() => setDashDateRangeDays(pOpt.value)}
                    className={`btn py-2 px-3.5 text-xs font-semibold rounded-pill border ${
                      dashDateRangeDays === pOpt.value
                        ? 'btn-dark text-white border-dark'
                        : 'btn-outline-secondary border-light-subtle text-muted bg-light bg-opacity-10'
                    }`}
                  >
                    {pOpt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preset Live Preview Sidebar Card */}
          <div className="col-12 col-xl-4">
            <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded-3 h-100">
              <h5 className="fw-bold font-sans text-dark mb-1" style={{ fontSize: '1.02rem' }}>
                Pré-visualização do Layout
              </h5>
              <p className="text-xs text-muted mb-4">Esta será a composição de cards renderizada para a equipe ao abrir a página inicial.</p>

              {/* Mock Dashboard Representation */}
              <div className="border border-light-subtle bg-light bg-opacity-30 rounded-3 p-3 text-dark font-sans shadow-inner">
                <div className="border-bottom border-secondary border-opacity-10 pb-2 mb-3">
                  <div className="text-xxs font-mono text-muted uppercase">PÁGINA INICIAL</div>
                  <div className="fw-bold text-dark font-display text-sm">performance da produção</div>
                </div>

                {dashActivePreset === 'SETORES_GARGALOS' && (
                  <div className="animate-fade-in">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Produção Média</span>
                          <span className="fw-black text-primary text-sm">16 ped.</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Tempo Médio</span>
                          <span className="fw-black text-dark text-sm">4h 44m</span>
                        </div>
                      </div>
                    </div>
                    <div className="card border border-light bg-white p-2.5 rounded mb-2">
                      <span className="text-xxs fw-bold text-muted d-block uppercase mb-1">Gargalo Identificado</span>
                      <div className="progress rounded-pill mb-1" style={{ height: '6px' }}>
                        <div className="progress-bar bg-danger" style={{ width: '85%' }}></div>
                      </div>
                      <span className="font-mono text-xxs text-danger fw-bold">SOLDA : 58min em média</span>
                    </div>
                  </div>
                )}

                {dashActivePreset === 'SLAS_ENTREGAS' && (
                  <div className="animate-fade-in">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Taxa de Atraso</span>
                          <span className="fw-black text-danger text-sm">25%</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Críticos</span>
                          <span className="fw-black text-danger text-sm">2 ped.</span>
                        </div>
                      </div>
                    </div>
                    <div className="card border border-light bg-white p-2 rounded mb-1">
                      <span className="text-xxs text-muted d-block">Urgente Próximo Vencimento</span>
                      <span className="fw-bold text-dark text-xxs d-block">Restaurante A (#621)</span>
                      <span className="font-mono text-xxs text-warning-emphasis fw-bold">SLA: Expira hoje</span>
                    </div>
                  </div>
                )}

                {dashActivePreset === 'VOLUME_METAS' && (
                  <div className="animate-fade-in">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Coifas Concluídas</span>
                          <span className="fw-black text-success text-sm">9 itens</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="card border border-light-subtle p-2 text-center bg-white rounded">
                          <span className="text-xxs text-muted d-block">Meta Cobertura</span>
                          <span className="fw-black text-success text-sm">64%</span>
                        </div>
                      </div>
                    </div>
                    <div className="card border border-light bg-white p-2 rounded">
                      <span className="text-xxs text-muted d-block">Mix Campeão de Vendas</span>
                      <span className="fw-bold text-dark text-xxs d-block">Coifa Alfa (Industrial)</span>
                      <span className="text-xxs text-muted">7 unidades faturadas</span>
                    </div>
                  </div>
                )}

                <div className="border-top pt-2 mt-3 d-flex justify-content-between text-muted font-mono" style={{ fontSize: '0.65rem' }}>
                  <span>Filtro: {dashDateRangeDays} dias</span>
                  <span className="text-uppercase text-decoration-none">modo ativo</span>
                </div>
              </div>

              {/* Action buttons inside sidebar */}
              <div className="mt-4 pt-3 border-top">
                <button
                  type="button"
                  disabled={isUpdatingDashConfig}
                  onClick={() => handleUpdateDashboardConfig(dashActivePreset, dashDateRangeDays)}
                  className="btn btn-primary w-full py-2.5 text-xs text-white fw-bold d-flex align-items-center justify-content-center gap-2 rounded-3"
                  style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa' }}
                >
                  {isUpdatingDashConfig ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Gravando Configurações...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> Salvar Parâmetros e Preset
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 4c. RENDER REPORTS CONFIGURATION MANAGER (MODEL DE IMPRESSÃO)
  const renderReportsManager = () => {
    const handleReportsConfigSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const companyName = formData.get('companyName') as string;
      const footerText = formData.get('footerText') as string;

      handleUpdateReportsConfig(companyName, repLayoutType, footerText, repIncludeAuditLog);
    };

    return (
      <div id="reports-setup-settings-view" className="animate-fade-in py-1">
        {/* Header Title */}
        <div className="border-bottom pb-3 mb-4 d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h4 fw-black text-dark font-display mb-1 text-lowercase" style={{ letterSpacing: '-0.5px' }}>
              configuração dos modelos de relatório
            </h1>
            <p className="text-xs font-mono text-muted mb-0">Parametrização global de cabeçalhos, rodapés e regras de impressão</p>
          </div>
          <button
            onClick={() => setActiveView('DASHBOARD')}
            className="btn btn-outline-secondary btn-xs font-mono px-3 py-1.5 text-xs rounded d-flex align-items-center gap-1.5"
            type="button"
          >
            <ChevronLeft size={14} /> Voltar às Configurações
          </button>
        </div>

        {/* Success / Error Alerts */}
        {alertState && (
          <div className={`alert alert-${alertState.type} border-0 rounded-3 shadow-xs d-flex align-items-center gap-2 mb-4 py-2 px-3`} role="alert">
            <span className="font-sans text-xs fw-semibold">{alertState.message}</span>
          </div>
        )}

        <div className="row g-4 font-sans text-dark">
          {/* Main settings form */}
          <div className="col-12 col-xl-7">
            <form onSubmit={handleReportsConfigSubmit} className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded-3 h-100">
              <h5 className="fw-bold font-sans text-dark mb-1" style={{ fontSize: '1.05rem' }}>
                Definições Corporativas para Impressão
              </h5>
              <p className="text-xs text-muted mb-4">Estas variáveis definem como os relatórios operacionais serão catalogados e impressos em PDF para a chefia de logística e de produção.</p>

              <div className="mb-4">
                <label className="form-label text-xs font-mono text-muted uppercase tracking-wider mb-1">Nome da Empresa (Cabeçalho do Relatório)</label>
                <input 
                  type="text" 
                  name="companyName"
                  defaultValue={repCompanyName}
                  required
                  placeholder="Ex: Master Coifa Ltda • Unidade Industrial"
                  className="form-control text-xs font-sans py-2.5 rounded border border-light-subtle bg-white text-dark"
                />
                <span className="text-xxs text-muted font-mono mt-1 d-block">Aparece em destaque em caixa alta no topo das folhas físicas geradas.</span>
              </div>

              <div className="mb-4">
                <label className="form-label text-xs font-mono text-muted uppercase tracking-wider mb-1">Estilo Padrão de Impressão</label>
                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <label className={`card p-3 rounded cursor-pointer border text-center h-100 d-flex flex-column justify-content-between ${repLayoutType === 'CLASSIFICACAO_CRITICA' ? 'border-primary bg-primary bg-opacity-5' : 'border-light'}`}>
                      <input 
                        type="radio" 
                        name="layoutType"
                        value="CLASSIFICACAO_CRITICA"
                        checked={repLayoutType === 'CLASSIFICACAO_CRITICA'}
                        onChange={() => setRepLayoutType('CLASSIFICACAO_CRITICA')}
                        className="d-none"
                      />
                      <span className="fw-bold text-xs d-block mb-1">Criticidade</span>
                      <span className="text-xxs font-mono text-muted d-block leading-normal">Ordena por prazos e sinaliza itens em Emergência de forma nítida.</span>
                    </label>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className={`card p-3 rounded cursor-pointer border text-center h-100 d-flex flex-column justify-content-between ${repLayoutType === 'SIMPLIFICADO_RESUMENTE' ? 'border-primary bg-primary bg-opacity-5' : 'border-light'}`}>
                      <input 
                        type="radio" 
                        name="layoutType"
                        value="SIMPLIFICADO_RESUMENTE"
                        checked={repLayoutType === 'SIMPLIFICADO_RESUMENTE'}
                        onChange={() => setRepLayoutType('SIMPLIFICADO_RESUMENTE')}
                        className="d-none"
                      />
                      <span className="fw-bold text-xs d-block mb-1">Ficha Neutra</span>
                      <span className="text-xxs font-mono text-muted d-block leading-normal">Cabeçalho limpo, tabelas compactas sem ressaltos coloridos.</span>
                    </label>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className={`card p-3 rounded cursor-pointer border text-center h-100 d-flex flex-column justify-content-between ${repLayoutType === 'DETALHADO_AUDITORIA' ? 'border-primary bg-primary bg-opacity-5' : 'border-light'}`}>
                      <input 
                        type="radio" 
                        name="layoutType"
                        value="DETALHADO_AUDITORIA"
                        checked={repLayoutType === 'DETALHADO_AUDITORIA'}
                        onChange={() => setRepLayoutType('DETALHADO_AUDITORIA')}
                        className="d-none"
                      />
                      <span className="fw-bold text-xs d-block mb-1">Auditoria Lote</span>
                      <span className="text-xxs font-mono text-muted d-block leading-normal">Imprime o histórico cronológico de períodos de cada operador.</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label text-xs font-mono text-muted uppercase tracking-wider mb-1">Termos e Assinatura do Rodapé</label>
                <textarea 
                  name="footerText"
                  defaultValue={repFooterText}
                  required
                  rows={3}
                  placeholder="Ex: Assinatura do encarregado de expedição..."
                  className="form-control text-xs font-sans py-2.5 rounded border border-light-subtle bg-white text-dark"
                />
                <span className="text-xxs text-muted font-mono mt-1 d-block">Isso garante proteção legal e formaliza a validação do gestor no chão de fábrica.</span>
              </div>

              <div className="mb-4">
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input cursor-pointer" 
                    type="checkbox" 
                    name="includeAuditLog" 
                    id="flexSwitchAuditLog" 
                    checked={repIncludeAuditLog}
                    onChange={(e) => setRepIncludeAuditLog(e.target.checked)}
                  />
                  <label className="form-check-input-label text-xs font-sans fw-semibold cursor-pointer" htmlFor="flexSwitchAuditLog">
                    Anexar Logs de Operação por Lote
                  </label>
                </div>
                <span className="text-xxs text-muted font-mono mt-1 d-block">Inclui cronologia de tempos de início e pausas no rodapé da impressão para auditorias físicas.</span>
              </div>

              <div className="mt-auto pt-3 border-top d-flex gap-2.5">
                <button
                  type="submit"
                  disabled={isUpdatingRepConfig}
                  className="btn btn-primary px-4 py-2.5 text-xs text-white fw-bold d-flex align-items-center gap-2 rounded border-0"
                  style={{ backgroundColor: '#2f66aa' }}
                >
                  {isUpdatingRepConfig ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Gravando Modelo...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> Salvar Parâmetros Globais
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Quick Real-Time Print Draft Preview */}
          <div className="col-12 col-xl-5">
            <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded-3 h-100">
              <h5 className="fw-bold font-sans text-dark mb-1" style={{ fontSize: '1.02rem' }}>
                Ficha de Pré-Visualização Física (A4)
              </h5>
              <p className="text-xs text-muted mb-4">Veja como será renderizada a folha padrão ao apertar Ctrl+P ou solicitar salvamento em PDF.</p>

              {/* Render Simulation paper sheets */}
              <div className="border border-secondary border-opacity-20 bg-light p-3 rounded-2 shadow-inner position-relative text-dark" style={{ minHeight: '390px', fontSize: '0.73rem' }}>
                
                {/* Print Sheet Simulated Grid in A4 style */}
                <div className="bg-white p-3 border rounded shadow-xs h-100 d-flex flex-column justify-content-between" style={{ minHeight: '350px' }}>
                  
                  {/* Simulated Header block */}
                  <div className="border-bottom border-dark border-2 pb-2 mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-1 text-dark uppercase fw-black" style={{ fontSize: '0.82rem', letterSpacing: '-0.3px', fontFamily: 'var(--font-sans)' }}>
                      <span>{repCompanyName || 'Master Coifa Ltda'}</span>
                      <Printer size={13} className="text-secondary" />
                    </div>
                    <div className="d-flex justify-content-between text-xxs font-mono text-muted mb-0">
                      <span>RELATÓRIO DE FISCALIZAÇÃO</span>
                      <span>DATA: {new Date().toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>

                  {/* Simulated Body Content */}
                  <div className="flex-grow-1">
                    <div className="bg-light p-2 mb-2 rounded border border-light-subtle">
                      <div className="fw-bold text-dark text-xxs uppercase mb-1">Modelo de Impressão:</div>
                      <span className="font-mono text-xxs text-primary d-block font-bold">
                        {repLayoutType === 'CLASSIFICACAO_CRITICA' && '● Criticidade & Balanço Cronológico'}
                        {repLayoutType === 'SIMPLIFICADO_RESUMENTE' && '● Ficha Neutra de Tabela Direta'}
                        {repLayoutType === 'DETALHADO_AUDITORIA' && '● Auditoria de Lote & Histórico'}
                      </span>
                    </div>

                    {/* Simulated tables line */}
                    <div className="font-mono mb-2 border-bottom pb-1" style={{ fontSize: '0.65rem' }}>
                      <div className="d-flex justify-content-between fw-bold text-muted uppercase">
                        <span>LOTE / CLIENTE</span>
                        <span>SALA / PRAZO</span>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom border-light text-dark">
                        <span>#104 - Restaurante Sabor</span>
                        <span className="fw-semibold">Corte / 3 dias</span>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom border-light text-dark">
                        <span>#105 - Gourmet SP</span>
                        <span className="text-danger fw-bold">Solda / Emergência</span>
                      </div>
                    </div>

                    {repIncludeAuditLog && (
                      <div className="bg-light p-2 rounded text-xxs mb-2 border">
                        <div className="fw-bold text-muted font-mono leading-none mb-1 text-uppercase" style={{ fontSize: '0.6rem' }}>LOGS DE AUDITORIA DE LOTE ATIVOS</div>
                        <div className="font-mono text-xxs text-secondary">
                          - 22/06 08:00 | Início do Corte por Gabriel<br/>
                          - 23/06 13:00 | Início da Solda por Paulo
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Simulated Footer block with validation signature space */}
                  <div className="border-top border-dark border-1 pt-2 mt-3 font-sans">
                    <p className="text-xxs text-secondary mb-3 leading-tight font-mono">{repFooterText || 'Relatório operacional sigiloso para controle de fluxo físico.'}</p>
                    
                    <div className="row g-2 text-center text-xxs font-mono">
                      <div className="col-6">
                        <div className="border-top pt-1 text-muted" style={{ fontSize: '0.55rem' }}>Assinatura do Encarregado</div>
                      </div>
                      <div className="col-6">
                        <div className="border-top pt-1 text-muted" style={{ fontSize: '0.55rem' }}>Carimbo da Fábrica</div>
                      </div>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Switch display panel
  const renderActiveContainer = () => {
    switch (activeView) {
      case 'PROJECTION_MGR':
        return renderProjectionManager();
      case 'SETORES':
        return renderSectorPanel();
      case 'USUARIOS':
        return renderUsersListPanel();
      case 'USUARIO_EDIT':
        return renderUserEditPanel();
      case 'STATUS_LIST':
        return renderStatusListPanel();
      case 'STATUS_EDIT':
        return renderStatusEditPanel();
      case 'DASHBOARD_PERF_MGR':
        return renderDashboardPerformanceManager();
      case 'REPORTS_MGR':
        return renderReportsManager();
      case 'SYSTEM_LOGS':
        return <SystemLogView onBack={() => setActiveView('DASHBOARD')} />;
      case 'OCCURRENCES':
        return <OccurrencesView onBack={() => setActiveView('DASHBOARD')} />;
      case 'DASHBOARD':
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="container-fluid py-2" id="settings-container-viewport">
      {renderActiveContainer()}
    </div>
  );
}
